import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DocumentDatesPanel from '../components/DocumentDatesPanel';   // mig 387
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { ProfitabilityContent } from './Profitability';
import CashBankDaybook from '../components/CashBankDaybook';
import PDCRegister from '../components/PDCRegister';
import OrderMargin from '../components/OrderMargin';
import AgingTab from '../components/AgingTab';
import CashFlowForecast from '../components/CashFlowForecast';
import CFOCockpit from '../components/CFOCockpit';
import ManageBanks from '../components/ManageBanks';
import CreditNoteQueue from '../components/CreditNoteQueue';
import BillAllocation from '../components/BillAllocation';   // mig 344 — a cheque can pay a bill
import SalesCertification from '../components/SalesCertification';   // mig 346 — ruling 22
import DebitNoteCertification from '../components/DebitNoteCertification';   // mig 351 — ruling 23
import DeliveryCentres from '../components/DeliveryCentres';
import ChartOfAccounts from '../components/ChartOfAccounts';   // accounting stage 1 — chart, import, mapping
import { KpiCard } from '../ui';
import PartyTypeahead from '../components/PartyTypeahead';   // Phase 1c — party picker, shared with the Day Book
import { askReason, whyNotLoaded } from '../utils/reportError';

// Finance — Sales Plan · Budget · Stock Register · Sales Invoices · Purchase Invoices.
// Access: admin, management, stores_incharge.

const API_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
const fileHref = (u) => !u ? '#' : (/^https?:\/\//.test(u) ? u : (u.startsWith('/api/') ? API_ORIGIN + u : u));
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const inr = (v) => '₹' + num(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
const today = () => new Date().toISOString().slice(0, 10);

const inputStyle = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const labelStyle = { fontSize: 9, color: '#888', fontWeight: 600, display: 'block', marginBottom: 2, textTransform: 'uppercase' };
const btnPrimary = { padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };
const card = { background: 'white', borderRadius: 10, padding: '14px 18px', boxShadow: 'none', border: '1px solid #E8E7E7' };
const thS = { padding: '8px 9px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const tdS = { padding: '8px 9px' };
const PAY_TAG = { unpaid: { bg: '#FCEBEB', c: '#A32D2D' }, partial: { bg: '#FFF8E6', c: '#9A6B00' }, paid: { bg: '#EAF7EE', c: '#166534' } };
const payTag = (s) => <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: (PAY_TAG[s] || {}).bg, color: (PAY_TAG[s] || {}).c }}>{(s || '').toUpperCase()}</span>;

// What each kind of bill hold asks Accounts to check (mig 377, 385).
const HOLD_LABEL = {
  do_not_pay: '⛔ DO NOT PAY', check_quantity: '⚠ CHECK QUANTITY', check_duplicate: '⚠ POSSIBLE DUPLICATE',
  check_payment: '⚠ MAY BE PAID ALREADY', check_amount: '⚠ CHECK AMOUNT', check_po: '⚠ CHECK PO',
};

function FileBtn({ onUploaded, label }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(r.data.url, f.name);
    } catch (err) { alert('Upload failed'); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <label style={{ ...btnPrimary, background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', display: 'inline-block' }}>
      {busy ? '…' : label}<input type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={upload} />
    </label>
  );
}

// Excel 2003 XML export for arbitrary rows.
function exportXls(filename, columns, rows) {
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const cell = (v) => `<Cell><Data ss:Type="${typeof v === 'number' ? 'Number' : 'String'}">${esc(v)}</Data></Cell>`;
  const xml = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="Export"><Table>
<Row>${columns.map(cell).join('')}</Row>
${rows.map(r => `<Row>${r.map(cell).join('')}</Row>`).join('')}
</Table></Worksheet></Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  URL.revokeObjectURL(a.href);
}

export default function Finance() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMgmt = ['admin', 'management'].includes(user?.role);
  // Byte-identical to PAY_DISBURSE / PURCHASE_ADJUST in finance.routes.js and PAY in
  // requests.routes.js. Management approves; accounts disburses. A screen that offered the
  // button to management would only produce a 403, and one that hid it from accounts is what
  // left them with the request route that refuses partial payment.
  const canDisburse = ['accounts', 'admin'].includes(user?.role);
  // Who may RAISE a release — the server's PAYMENT_RAISE_ROLES, stated for the button only; the route decides.
  const canRaise = ['admin', 'management', 'accounts', 'finance_approver', 'stores_incharge', 'procurement_officer',
    'production_incharge', 'ppc_manager', 'maintenance_incharge', 'merchandiser', 'deroi_operator'].includes(user?.role);
  const isFin = ['admin', 'management', 'accounts'].includes(user?.role);   // can manage the Day Book
  // HR reaches Finance for ONE reason: verifying Day Book entries (mig 176). It gets that tab
  // and nothing else — no cockpit, no margin, no PDC, not even the Sales Plan / Budget / Stock
  // tabs that are otherwise open to everyone here.
  const isDaybookVerifier = user?.role === 'hr';
  // Deroi reaches Finance for ONE reason too: seeing what Deroi owes. It gets Purchase Invoices
  // and nothing else — no cockpit, no day book, no margin, no sales invoices, and not even the
  // four company-wide KPI tiles, which would read as RSR's numbers to a Deroi operator.
  // The backend gate is separate and narrower still (PAYABLES_READ on the list route only), so
  // this is presentation, not the control.
  const isDeroiPayables = user?.role === 'deroi_operator';
  const isBankAdmin = ['admin', 'accounts'].includes(user?.role);           // can manage bank accounts
  const isCert = ['admin', 'management', 'accounts', 'finance_approver'].includes(user?.role);   // GRC certify (=CERT)
  const [tab, setTab] = useState(['admin', 'management', 'accounts'].includes(user?.role) ? 'cockpit'
    : user?.role === 'hr' ? 'daybook' : user?.role === 'deroi_operator' ? 'purchase_inv' : 'sales_plan');
  const [kpis, setKpis] = useState(null);
  const [kpisErr, setKpisErr] = useState('');
  const [gpos, setGpos] = useState([]);

  // The four cards follow whichever tab's picker is open. A tab without one reports nothing
  // and they fall back to the viewer's own book, which is what they have always shown.
  const [kpiBook, setKpiBook] = useState('');
  // NEVER THE PREVIOUS BOOK'S FIGURES UNDER THE NEW BOOK'S NAME. The cards are cleared before each
  // read, a failed read leaves them empty with its reason, and a late answer for a book no longer
  // selected is dropped. It used to swallow the failure and keep whatever was on screen.
  useEffect(() => {
    let current = true;
    setKpis(null); setKpisErr('');
    api.get('/finance/kpis', kpiBook ? { headers: { 'X-Company-Id': kpiBook } } : {})
      .then(r => { if (current) setKpis(r.data.data); })
      .catch(e => { if (current) setKpisErr(whyNotLoaded(e)); });
    return () => { current = false; };
  }, [tab, kpiBook]);
  // The GPO list is not company-scoped and does not belong on the book's dependency array —
  // re-fetching 136 orders every time someone flips the picker would be pure waste.
  useEffect(() => {
    api.get('/oe/gpos').then(r => setGpos((r.data.data || []).filter(g => !g.is_superseded))).catch(() => {});
  }, [tab]);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const tabs = isDaybookVerifier ? [{ key: 'daybook', label: '💵 Cash & Bank Day Book' }]
    : isDeroiPayables ? [{ key: 'purchase_inv', label: '📑 Purchase Invoices' }] : [
    ...(isFin ? [{ key: 'cockpit', label: '🏦 CFO Cockpit' }, { key: 'daybook', label: '💵 Cash & Bank Day Book' }, { key: 'margin', label: '📊 Order Margin' }, { key: 'aging', label: '⏳ Payables & Receivables' }, { key: 'forecast', label: '💧 Cash-Flow Forecast' }, { key: 'pdc', label: '🧾 PDC Register' }, { key: 'allocation', label: '🔗 Bill-wise Allocation' }, { key: 'credit_notes', label: '↩️ Credit Notes' }, { key: 'delivery_centres', label: '📍 Delivery Centres' }, { key: 'parties', label: '🔗 Party ↔ Vendor' }] : []),
    ...(isCert ? [{ key: 'grc', label: '✅ GRC Certification' }, { key: 'sales_cert', label: '🧾 Sales Certification' }, { key: 'dn_cert', label: '↩️ Debit Note Certification' }] : []),
    ...(isBankAdmin ? [{ key: 'banks', label: '🏦 Manage Banks' }] : []),
    // Chart of Accounts: read for admin/management/accounts (= isFin); changes for admin/accounts only,
    // enforced by the server and explained on the screen.
    ...(isFin ? [{ key: 'coa', label: '📒 Chart of Accounts' }] : []),
    { key: 'sales_plan', label: '📈 Sales Plan' },
    { key: 'budget', label: '💰 Budget' },
    { key: 'stock', label: '📦 Stock Register' },
    { key: 'sales_inv', label: '🧾 Sales Invoices' },
    { key: 'purchase_inv', label: '📑 Purchase Invoices' },
    ...(isMgmt ? [{ key: 'profitability', label: '📊 Profitability' }] : []),
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      

      <div style={{ padding: 24 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 20, color: '#1a1a2e' }}>💼 Finance</h2>
        {/* KPI cards — hidden for the Day Book verifier, who has no access to /finance/kpis and
            would otherwise be shown four permanently-empty company-wide finance tiles. */}
        {!isDaybookVerifier && !isDeroiPayables && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 18 }}>
            {[['Total Sales This Month', kpis?.sales_this_month, '#E22D2D'], ['Total Outstanding', kpis?.total_outstanding, '#BA7517'],
              ['Total Payable', kpis?.total_payable, '#BA7517'], ['Stock Value Today', kpis?.stock_value_today, '#1D9E75']].map(([l, v, c]) => (
              <KpiCard key={l} label={l} value={v !== undefined && v !== null ? inr(v) : '—'} color={c} />
            ))}
          </div>
        )}
        {!isDaybookVerifier && !isDeroiPayables && kpisErr && (
          <div style={{ margin: '-8px 0 16px', fontSize: 12, color: '#A32D2D' }}>
            The figures above are not shown: they could not be loaded for this book ({kpisErr}).
          </div>
        )}

        <div style={{ background: 'white', borderRadius: 10, boxShadow: 'none', border: '1px solid #E8E7E7', overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee', padding: '0 12px', flexWrap: 'wrap' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '13px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13,
                fontWeight: tab === t.key ? 700 : 400, color: tab === t.key ? '#3B3A8F' : '#888',
                borderBottom: tab === t.key ? '2px solid #3B3A8F' : '2px solid transparent', whiteSpace: 'nowrap' }}>{t.label}</button>
            ))}
          </div>
          <div style={{ padding: 18, overflowX: 'auto' }}>
            {tab === 'daybook' && <CashBankDaybook />}
            {tab === 'margin' && <OrderMargin />}
            {tab === 'cockpit' && <CFOCockpit />}
            {tab === 'grc' && <GrcCertificationTab />}
            {tab === 'sales_cert' && <SalesCertification />}
            {tab === 'dn_cert' && <DebitNoteCertification />}
            {tab === 'aging' && <AgingTab />}
            {tab === 'forecast' && <CashFlowForecast />}
            {tab === 'pdc' && <PDCRegister />}
            {tab === 'allocation' && <BillAllocation />}
            {tab === 'credit_notes' && <CreditNoteQueue />}
            {tab === 'delivery_centres' && <DeliveryCentres />}
            {tab === 'parties' && <PartyVendorLink />}
            {tab === 'banks' && <ManageBanks />}
            {tab === 'coa' && <ChartOfAccounts />}
            {tab === 'sales_plan' && <SalesPlanTab isMgmt={isMgmt} />}
            {tab === 'budget' && <BudgetTab gpos={gpos} isMgmt={isMgmt} />}
            {tab === 'stock' && <StockTab user={user} gpos={gpos} onBook={setKpiBook} />}
            {tab === 'sales_inv' && <SalesInvTab gpos={gpos} onBook={setKpiBook} />}
            {tab === 'purchase_inv' && <PurchaseInvTab isMgmt={isMgmt} canDisburse={canDisburse} canRaise={canRaise} onBook={setKpiBook} />}
            {tab === 'profitability' && isMgmt && <ProfitabilityContent />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══ Tab 1 — Sales Plan (all merchandisers) ═══
function SalesPlanTab({ isMgmt }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [merchs, setMerchs] = useState([]);
  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));
    const r = await api.get('/finance/sales-plan?' + qs.toString()).catch(() => ({ data: { data: [] } }));
    setRows(r.data.data || []);
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const m = {}; rows.forEach(r => { m[r.merchandiser_id] = r.merchandiser_name; });
    setMerchs(Object.entries(m));
  }, [rows]);

  // Merchandiser-wise summary.
  const byMerch = {};
  rows.forEach(r => {
    const k = r.merchandiser_name || '—';
    if (!byMerch[k]) byMerch[k] = { plans: 0, planned: 0, actual: 0 };
    byMerch[k].plans++; byMerch[k].planned += num(r.planned_revenue_inr); byMerch[k].actual += num(r.actual_revenue_inr);
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto', background: '#F7F6F3', padding: '6px 8px', borderRadius: 8 }}>
        <select style={{ ...inputStyle, width: 150 }} value={filters.merchandiser_id || ''} onChange={e => setFilters({ ...filters, merchandiser_id: e.target.value })}>
          <option value="">All merchandisers</option>{merchs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select>
        <input type="month" style={{ ...inputStyle, width: 140 }} value={filters.month || ''} onChange={e => setFilters({ ...filters, month: e.target.value })} />
        <input style={{ ...inputStyle, width: 100 }} placeholder="GPO…" value={filters.gpo_no || ''} onChange={e => setFilters({ ...filters, gpo_no: e.target.value })} />
        <select style={{ ...inputStyle, width: 120 }} value={filters.status || ''} onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>{['planned', 'partial', 'shipped', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}</select>
        {Object.values(filters).some(Boolean) && <button onClick={() => setFilters({})} style={{ ...inputStyle, cursor: 'pointer', background: 'white' }}>✕ Clear</button>}
        <button onClick={() => exportXls('sales-plan.xls',
          ['Plan No', 'GPO', 'Merchandiser', 'Month', 'Buyer', 'Style', 'Order Qty', 'Price/pc', 'Planned ₹', 'Actual Qty', 'Actual ₹', 'Variance ₹', 'Status'],
          rows.map(r => [r.plan_no, r.gpo_no, r.merchandiser_name, r.plan_month, r.buyer, r.style_no, num(r.order_qty), num(r.selling_price_per_pc), num(r.planned_revenue_inr), num(r.actual_qty_shipped), num(r.actual_revenue_inr), num(r.actual_revenue_inr) - num(r.planned_revenue_inr), r.status]))}
          style={{ ...btnPrimary, marginLeft: 'auto', background: '#166534', flex: '0 0 auto' }}>⬇ Export Excel</button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['GPO', 'Merchandiser', 'Buyer', 'Style', 'Month', 'Qty', 'Price/pc', 'Planned ₹', 'Actual Qty', 'Actual ₹', 'Variance', 'Status'].map(h => <th key={h} style={thS}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={12} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>No sales plans yet.</td></tr>
            : rows.map(r => {
              const variance = num(r.actual_revenue_inr) - num(r.planned_revenue_inr);
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{r.gpo_no}</td>
                  <td style={tdS}>{r.merchandiser_name || '—'}</td>
                  <td style={tdS}>{r.buyer || '—'}</td>
                  <td style={tdS}>{r.style_no || '—'}</td>
                  <td style={tdS}>{r.plan_month}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{num(r.order_qty).toLocaleString('en-IN')}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{inr(r.selling_price_per_pc)}</td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{inr(r.planned_revenue_inr)}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{r.actual_qty_shipped ?? '—'}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{r.actual_revenue_inr ? inr(r.actual_revenue_inr) : '—'}</td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: variance >= 0 ? '#166534' : '#A32D2D' }}>{r.actual_revenue_inr ? inr(variance) : '—'}</td>
                  <td style={tdS}>{r.status}</td>
                </tr>
              );
            })}
        </tbody>
      </table>

      {Object.keys(byMerch).length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Merchandiser-wise Summary</div>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 520 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>{['Merchandiser', 'Plans', 'Planned ₹', 'Actual ₹', 'Variance'].map(h => <th key={h} style={thS}>{h}</th>)}</tr></thead>
            <tbody>{Object.entries(byMerch).map(([name, s]) => (
              <tr key={name} style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ ...tdS, fontWeight: 700 }}>{name}</td><td style={{ ...tdS, textAlign: 'right' }}>{s.plans}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{inr(s.planned)}</td><td style={{ ...tdS, textAlign: 'right' }}>{inr(s.actual)}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: s.actual - s.planned >= 0 ? '#166534' : '#A32D2D' }}>{inr(s.actual - s.planned)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══ Tab 2 — Budget ═══
function BudgetTab({ gpos, isMgmt }) {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [manualActuals, setManualActuals] = useState({});
  useEffect(() => {
    if (detail) setManualActuals({ labour_actual: num(detail.labour_actual) || '', overhead_actual: num(detail.overhead_actual) || '' });
  }, [detail?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    const r = await api.get('/finance/budget').catch(() => ({ data: { data: [] } }));
    setRows(r.data.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const CATS = [['fabric', 'Fabric'], ['trims', 'Trims'], ['job_work', 'Job Work'], ['labour', 'Labour'], ['overhead', 'Overhead']];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button onClick={() => setShowNew(true)} style={btnPrimary}>+ New Budget</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['Budget No', 'GPO', 'Total Budget', 'Total Actual', 'Variance', 'Status', ''].map(h => <th key={h} style={thS}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={7} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>No budgets yet.</td></tr>
            : rows.map(b => (
              <tr key={b.id} onClick={() => setDetail(b)} style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: num(b.variance) < 0 ? '#FFF8F8' : 'white' }}>
                <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{b.budget_no}</td>
                <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{b.gpo_no}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{inr(b.total_budget)}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{inr(b.total_actual)}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 800, color: num(b.variance) >= 0 ? '#166534' : '#A32D2D' }}>
                  {num(b.variance) >= 0 ? inr(b.variance) + ' under' : inr(-b.variance) + ' OVER'}</td>
                <td style={tdS}><span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: b.status === 'approved' ? '#EAF7EE' : '#f0f2f8', color: b.status === 'approved' ? '#166534' : '#666' }}>{b.status}</span></td>
                <td style={tdS} onClick={e => e.stopPropagation()}>
                  {isMgmt && b.status === 'draft' && <button onClick={async () => { await api.patch(`/finance/budget/${b.id}/approve`).catch(e => alert(e.response?.data?.error)); load(); }}
                    style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: '#166534' }}>✓ Approve</button>}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={() => setDetail(null)}>
          <div style={{ background: 'white', borderRadius: 12, width: 560, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{detail.budget_no} · GPO {detail.gpo_no}</h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888' }}>Budget vs Actual (actuals from PO + JWO invoices)</p>
            {CATS.map(([k, l]) => {
              const bud = num(detail[k + '_budget']), act = num(detail[k + '_actual']);
              const pct = bud > 0 ? Math.min(150, act / bud * 100) : (act > 0 ? 150 : 0);
              const over = act > bud;
              return (
                <div key={k} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                    <span style={{ fontWeight: 600 }}>{l}</span>
                    <span>{inr(act)} / {inr(bud)} <strong style={{ color: over ? '#A32D2D' : '#166534' }}>({inr(bud - act)} {over ? 'over' : 'left'})</strong></span>
                  </div>
                  <div style={{ background: '#f0f2f8', borderRadius: 5, height: 10, overflow: 'hidden' }}>
                    <div style={{ width: Math.min(100, pct) + '%', height: '100%', background: over ? '#E22D2D' : '#16a34a' }} />
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 800 }}>
              <span>TOTAL</span>
              <span style={{ color: num(detail.variance) >= 0 ? '#166534' : '#A32D2D' }}>{inr(detail.total_actual)} / {inr(detail.total_budget)}</span>
            </div>
            {/* Labour/overhead have no invoice source — management enters actuals manually. */}
            {isMgmt && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: '#FBFBFE', border: '1px dashed #c7d2fe', borderRadius: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#3B3A8F', marginBottom: 6 }}>MANUAL ACTUALS (no invoice source)</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div><label style={labelStyle}>Labour Actual ₹</label>
                    <input type="number" style={{ ...inputStyle, width: 130 }} value={manualActuals.labour_actual ?? ''} onChange={e => setManualActuals(m => ({ ...m, labour_actual: e.target.value }))} /></div>
                  <div><label style={labelStyle}>Overhead Actual ₹</label>
                    <input type="number" style={{ ...inputStyle, width: 130 }} value={manualActuals.overhead_actual ?? ''} onChange={e => setManualActuals(m => ({ ...m, overhead_actual: e.target.value }))} /></div>
                  <button onClick={async () => {
                    try {
                      const r = await api.patch('/finance/budget/' + detail.id, manualActuals);
                      setDetail(r.data.data); load();
                    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
                  }} style={{ ...btnPrimary, background: '#166534' }}>💾 Save Actuals</button>
                </div>
              </div>
            )}
            <div style={{ textAlign: 'right', marginTop: 14 }}>
              <button onClick={() => setDetail(null)} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showNew && <NewBudgetModal gpos={gpos} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewBudgetModal({ gpos, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [gpoInfo, setGpoInfo] = useState(null);
  const onGpo = async (gpoNo) => {
    setForm(f => ({ ...f, gpo_no: gpoNo }));
    const g = gpos.find(x => x.gpo_no === gpoNo);
    setGpoInfo(g);
    // Suggest amounts from the GPO's cost estimation (per-pc × order qty).
    if (g?.estimate_no) {
      try {
        const r = await api.get('/oe/estimates/by-no/' + encodeURIComponent(g.estimate_no));
        const ce = r.data.data; const qty = num(g.order_qty);
        if (ce) setForm(f => ({ ...f,
          fabric_budget: +(num(ce.fabric_cost) * qty).toFixed(0),
          trims_budget: +(num(ce.accessories_total) * qty).toFixed(0),
          job_work_budget: +(num(ce.job_work_cost) * qty).toFixed(0),
          labour_budget: +(num(ce.labour_total) * qty).toFixed(0),
          // Per piece AS THE ESTIMATE USED IT (production cost less its other parts), never the
          // typed overhead_total, which on many estimates is an order total (8 Oct 2026).
          overhead_budget: +(Math.max(0, num(ce.production_cost) - num(ce.fabric_cost) - num(ce.accessories_total)
            - num(ce.labour_total) - num(ce.job_work_cost)) * qty).toFixed(0),
        }));
      } catch {}
    }
  };
  const total = ['fabric', 'trims', 'job_work', 'labour', 'overhead'].reduce((s, k) => s + num(form[k + '_budget']), 0);
  const save = async () => {
    setBusy(true); setError('');
    try { await api.post('/finance/budget', form); onSaved(); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 480, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>＋ New Budget</h3></div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <label style={labelStyle}>GPO No * (one budget per GPO)</label>
        <select style={{ ...inputStyle, width: '100%', marginBottom: 8 }} value={form.gpo_no || ''} onChange={e => onGpo(e.target.value)}>
          <option value="">— select —</option>{gpos.map(g => <option key={g.id} value={g.gpo_no}>{g.gpo_no} · {g.style_no || ''}</option>)}</select>
        {gpoInfo && <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Style {gpoInfo.style_no || '—'} · {num(gpoInfo.order_qty).toLocaleString('en-IN')} pcs · suggestions from Cost Estimation</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[['fabric_budget', 'Fabric ₹'], ['trims_budget', 'Trims ₹'], ['job_work_budget', 'Job Work ₹ (manual)'], ['labour_budget', 'Labour ₹'], ['overhead_budget', 'Overhead ₹']].map(([k, l]) => (
            <div key={k}><label style={labelStyle}>{l}</label>
              <input type="number" style={{ ...inputStyle, width: '100%' }} value={form[k] ?? ''} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
          ))}
          <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '8px 12px', alignSelf: 'end' }}>
            <span style={{ fontSize: 10, color: '#888' }}>TOTAL </span><strong style={{ fontSize: 16, color: '#3B3A8F' }}>{inr(total)}</strong></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? '…' : '💾 Create Budget'}</button>
        </div>
      </div>
    </div>
  );
}

// ═══ Tab 3 — Stock Register ═══
// ─── ONE PICKER, THREE TABS ──────────────────────────────────────────────────────────────────
// PurchaseInvTab grew this first and SalesInvTab and StockTab need the identical thing. A third
// and fourth copy is how three screens come to disagree about who may switch — which is exactly
// what happened between the Day Book and payables and cost a day. The endpoint is still called
// /payables-companies; it now serves the whole finance module and the name is narrower than the
// thing. Left alone rather than renamed: the already-deployed bundle calls that path, and a
// rename would blank every picker for the length of a rollout.
function useFinanceBook(onBook) {
  const [companies, setCompanies] = useState([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [company, setCompany] = useState('');
  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);
  useEffect(() => {
    api.get('/finance/payables-companies').then(r => {
      setCompanies(r.data.data || []); setCanSwitch(!!r.data.can_switch);
      setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || '');
    }).catch(() => {});
  }, []);
  // Report upward so the page's KPI cards can follow whichever tab is open. A tab with no
  // picker reports nothing and the cards fall back to the viewer's own book.
  useEffect(() => { if (company && onBook) onBook(company); }, [company, onBook]);
  return { companies, canSwitch, company, setCompany, H };
}

// Disabled rather than hidden, so the screen always names the book it is showing — the same
// rule the gate register and the payables tab already follow.
function BookPicker({ companies, canSwitch, company, setCompany }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 11, color: '#888' }}>Book</span>
      <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)}
        title={canSwitch ? 'Which company\'s book this screen is showing' : 'Your account acts only for its own company'}
        style={{ padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontWeight: 700,
                 color: '#3B3A8F', background: canSwitch ? '#EEEDF8' : '#f5f5f5' }}>
        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </div>
  );
}

function StockTab({ user, gpos, onBook }) {
  const [sub, setSub] = useState('raw_material');
  const [rows, setRows] = useState([]);
  const [wipAuto, setWipAuto] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [autoMsg, setAutoMsg] = useState('');
  const { companies, canSwitch, company, setCompany, H } = useFinanceBook(onBook);
  const load = useCallback(async () => {
    if (!company) return;               // never ask before we know whose book to ask for
    // Raw material: auto-create register entries for today's GRNs first.
    if (sub === 'raw_material') {
      try {
        const a = await api.post('/finance/stock/auto-from-grn', {});
        if (a.data.created > 0) setAutoMsg(`✅ ${a.data.created} entr${a.data.created > 1 ? 'ies' : 'y'} auto-created from today's GRNs — edit Issued qty inline as material moves out.`);
      } catch (e) { setAutoMsg(`⚠ Could not auto-create entries from today's GRNs — ${e.response?.data?.error || e.message}. Enter them by hand or reload.`); }
    }
    const r = await api.get('/finance/stock?type=' + sub, H()).catch(() => ({ data: { data: [] } }));
    setRows(r.data.data || []);
    if (sub === 'wip') api.get('/finance/stock/wip-auto', H()).then(x => setWipAuto(x.data.data || [])).catch(() => {});
  }, [sub, company, H]);
  useEffect(() => { load(); }, [load]);
  const saveIssued = async (row, issued) => {
    if (num(issued) === num(row.issued_qty)) return;
    try { await api.patch('/finance/stock/' + row.id, { issued_qty: issued }); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const totalValue = rows.reduce((s, r) => s + num(r.stock_value), 0);
  const subs = [['raw_material', '🧵 Raw Material'], ['wip', '⚙️ WIP'], ['finished_goods', '📦 Finished Goods']];
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #eee', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div>{subs.map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} style={{ padding: '9px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: sub === k ? 700 : 400, color: sub === k ? '#3B3A8F' : '#888', borderBottom: sub === k ? '2px solid #3B3A8F' : '2px solid transparent' }}>{l}</button>
        ))}</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <BookPicker companies={companies} canSwitch={canSwitch} company={company} setCompany={setCompany} />
          <button onClick={() => setShowNew(true)} style={{ ...btnPrimary, alignSelf: 'center' }}>+ Add Stock Entry</button>
        </div>
      </div>

      {sub === 'raw_material' && autoMsg && (
        <div style={{ marginBottom: 12, background: '#EAF7EE', border: '1px solid #bbe5c5', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#166534', fontWeight: 700 }}>{autoMsg}</div>
      )}
      {sub === 'wip' && wipAuto.length > 0 && (
        <div style={{ marginBottom: 12, background: '#FAF7EE', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#854F0B' }}>
          ⚙️ <strong>From PPC plans:</strong> {wipAuto.map(w => `GPO ${w.gpo_no} — cut ${num(w.panels_cut)}, stitched ${num(w.stitched)}, finished ${num(w.finished)}`).join(' · ')}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {(sub === 'raw_material'
            ? ['Date', 'Item', 'Category', 'Opening', 'Received', 'Issued', 'Closing', 'Unit', 'Rate', 'Value', 'By']
            : sub === 'wip'
              ? ['Date', 'GPO', 'Panels Cut', 'Stitched', 'Finished', 'By']
              : ['Date', 'GPO', 'Style', 'Colour', 'Size Breakdown', 'Total Packed', 'Cartons', 'By']
          ).map(h => <th key={h} style={thS}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={11} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>No entries yet.</td></tr>
            : rows.map(r => {
              const ex = typeof r.extra === 'string' ? JSON.parse(r.extra || '{}') : (r.extra || {});
              return sub === 'raw_material' ? (
                <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={tdS}>{fmtD(r.entry_date)}</td><td style={{ ...tdS, fontWeight: 600 }}>{r.item_name}</td><td style={tdS}>{r.item_category}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{num(r.opening_stock).toLocaleString('en-IN')}</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#166534' }}>{num(r.received_qty).toLocaleString('en-IN')}{(typeof r.extra === 'string' ? JSON.parse(r.extra || '{}') : (r.extra || {})).grn_no ? <span style={{ fontSize: 9, color: '#3B3A8F', marginLeft: 4 }} title="Auto-created from GRN">🔗</span> : null}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>
                    <input type="number" step="0.001" defaultValue={num(r.issued_qty)} onBlur={e => saveIssued(r, e.target.value)}
                      style={{ width: 70, padding: '3px 6px', border: '1px solid #e3e3e3', borderRadius: 4, fontSize: 11, textAlign: 'right', color: '#A32D2D' }} title="Edit issued qty — saves on blur" />
                  </td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{num(r.closing_stock).toLocaleString('en-IN')}</td>
                  <td style={tdS}>{r.unit}</td><td style={{ ...tdS, textAlign: 'right' }}>{inr(r.rate)}</td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{inr(r.stock_value)}</td><td style={tdS}>{r.updated_by_name || '—'}</td>
                </tr>
              ) : sub === 'wip' ? (
                <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={tdS}>{fmtD(r.entry_date)}</td>
                  <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{r.gpo_no}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{num(ex.panels_cut).toLocaleString('en-IN')}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{num(ex.stitched).toLocaleString('en-IN')}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{num(ex.finished).toLocaleString('en-IN')}</td>
                  <td style={tdS}>{r.updated_by_name || '—'}</td>
                </tr>
              ) : (
                <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={tdS}>{fmtD(r.entry_date)}</td>
                  <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{r.gpo_no}</td>
                  <td style={tdS}>{ex.style || r.item_name || '—'}</td><td style={tdS}>{ex.colour || '—'}</td>
                  <td style={tdS}>{ex.size_breakdown || '—'}</td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{num(ex.total_packed).toLocaleString('en-IN')}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{num(ex.cartons).toLocaleString('en-IN')}</td>
                  <td style={tdS}>{r.updated_by_name || '—'}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
      {sub === 'raw_material' && rows.length > 0 && (
        <div style={{ marginTop: 10, textAlign: 'right', fontSize: 13, fontWeight: 800 }}>Total Stock Value: <span style={{ color: '#166534' }}>{inr(totalValue)}</span></div>
      )}
      {showNew && <StockEntryModal sub={sub} gpos={gpos} wipAuto={wipAuto} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function StockEntryModal({ sub, gpos, wipAuto, onClose, onSaved }) {
  const [form, setForm] = useState({ entry_date: today(), item_category: 'fabric' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closing = num(form.opening_stock) + num(form.received_qty) - num(form.issued_qty);
  const save = async () => {
    setBusy(true); setError('');
    const body = sub === 'raw_material' ? { ...form, entry_type: sub }
      : sub === 'wip'
        ? { entry_date: form.entry_date, entry_type: sub, gpo_no: form.gpo_no, extra: { panels_cut: num(form.panels_cut), stitched: num(form.stitched), finished: num(form.finished) } }
        : { entry_date: form.entry_date, entry_type: sub, gpo_no: form.gpo_no, item_name: form.style, extra: { style: form.style, colour: form.colour, size_breakdown: form.size_breakdown, total_packed: num(form.total_packed), cartons: num(form.cartons) } };
    try { await api.post('/finance/stock', body); onSaved(); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  const prefillWip = (gpoNo) => {
    const w = wipAuto.find(x => x.gpo_no === gpoNo);
    setForm(f => ({ ...f, gpo_no: gpoNo, ...(w ? { panels_cut: w.panels_cut, stitched: w.stitched, finished: w.finished } : {}) }));
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 460, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>＋ Stock Entry · {sub.replace('_', ' ')}</h3></div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>Date</label><input type="date" style={{ ...inputStyle, width: '100%' }} value={form.entry_date} onChange={e => setForm({ ...form, entry_date: e.target.value })} /></div>
          {sub === 'raw_material' ? <>
            <div><label style={labelStyle}>Category</label>
              <select style={{ ...inputStyle, width: '100%' }} value={form.item_category} onChange={e => setForm({ ...form, item_category: e.target.value })}>
                {['fabric', 'yarn', 'trims'].map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Item Name *</label><input style={{ ...inputStyle, width: '100%' }} value={form.item_name || ''} onChange={e => setForm({ ...form, item_name: e.target.value })} /></div>
            {[['opening_stock', 'Opening'], ['received_qty', 'Received (from GRNs today)'], ['issued_qty', 'Issued'], ['rate', 'Rate ₹']].map(([k, l]) => (
              <div key={k}><label style={labelStyle}>{l}</label><input type="number" step="0.001" style={{ ...inputStyle, width: '100%' }} value={form[k] ?? ''} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
            ))}
            <div><label style={labelStyle}>Unit</label><select style={{ ...inputStyle, width: '100%' }} value={form.unit || 'KG'} onChange={e => setForm({ ...form, unit: e.target.value })}>{['KG', 'PCS', 'MTR'].map(u => <option key={u}>{u}</option>)}</select></div>
            <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '6px 10px', alignSelf: 'end' }}>
              <span style={{ fontSize: 10, color: '#888' }}>CLOSING </span><strong>{closing.toLocaleString('en-IN')}</strong>
              <span style={{ fontSize: 10, color: '#888', marginLeft: 8 }}>VALUE </span><strong style={{ color: '#166534' }}>{inr(closing * num(form.rate))}</strong></div>
          </> : <>
            <div><label style={labelStyle}>GPO *</label>
              <select style={{ ...inputStyle, width: '100%' }} value={form.gpo_no || ''} onChange={e => sub === 'wip' ? prefillWip(e.target.value) : setForm({ ...form, gpo_no: e.target.value })}>
                <option value="">—</option>{gpos.map(g => <option key={g.id} value={g.gpo_no}>{g.gpo_no}</option>)}</select></div>
            {sub === 'wip' ? [['panels_cut', 'Panels Cut'], ['stitched', 'Stitched'], ['finished', 'Finished']].map(([k, l]) => (
              <div key={k}><label style={labelStyle}>{l} (PCS)</label><input type="number" style={{ ...inputStyle, width: '100%' }} value={form[k] ?? ''} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
            )) : <>
              {[['style', 'Style'], ['colour', 'Colour'], ['size_breakdown', 'Size Breakdown (S/M/L…)'], ['total_packed', 'Total Packed'], ['cartons', 'Cartons']].map(([k, l]) => (
                <div key={k}><label style={labelStyle}>{l}</label><input style={{ ...inputStyle, width: '100%' }} value={form[k] ?? ''} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
              ))}
            </>}
          </>}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? '…' : '💾 Save Entry'}</button>
        </div>
      </div>
    </div>
  );
}

// ═══ Tab 4 — Sales Invoices ═══
function SalesInvTab({ gpos, onBook }) {
  const { user } = useAuth();
  const canAdjust = ['accounts', 'admin'].includes(user?.role);   // edit / reverse gate
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [aging, setAging] = useState(null);
  const [filters, setFilters] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [payInv, setPayInv] = useState(null);
  const [editInv, setEditInv] = useState(null);
  const { companies, canSwitch, company, setCompany, H } = useFinanceBook(onBook);
  const load = useCallback(async () => {
    if (!company) return;               // never ask before we know whose book to ask for
    const qs = new URLSearchParams(); Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));
    const r = await api.get('/finance/sales-invoices?' + qs.toString(), H()).catch(() => ({ data: { data: [] } }));
    setRows(r.data.data || []); setSummary(r.data.summary); setAging(r.data.aging);
  }, [filters, company, H]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto', background: '#F7F6F3', padding: '6px 8px', borderRadius: 8 }}>
        <BookPicker companies={companies} canSwitch={canSwitch} company={company} setCompany={setCompany} />
        <input style={{ ...inputStyle, width: 130 }} placeholder="Buyer…" value={filters.buyer || ''} onChange={e => setFilters({ ...filters, buyer: e.target.value })} />
        <select style={{ ...inputStyle, width: 110 }} value={filters.status || ''} onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>{['unpaid', 'partial', 'paid'].map(s => <option key={s}>{s}</option>)}</select>
        <input style={{ ...inputStyle, width: 90 }} placeholder="GPO…" value={filters.gpo_no || ''} onChange={e => setFilters({ ...filters, gpo_no: e.target.value })} />
        <input type="date" style={{ ...inputStyle, width: 125 }} value={filters.from || ''} onChange={e => setFilters({ ...filters, from: e.target.value })} />
        <input type="date" style={{ ...inputStyle, width: 125 }} value={filters.to || ''} onChange={e => setFilters({ ...filters, to: e.target.value })} />
        {Object.values(filters).some(Boolean) && <button onClick={() => setFilters({})} style={{ ...inputStyle, cursor: 'pointer', background: 'white' }}>✕</button>}
        <button onClick={() => setShowNew(true)} style={{ ...btnPrimary, marginLeft: 'auto', flex: '0 0 auto' }} title="For a shipment that has already gone — writes the delivery challan and the invoice together">+ Record a Sale</button>
      </div>
      {summary && (
        <div style={{ display: 'flex', gap: 14, marginBottom: 12, fontSize: 12, flexWrap: 'wrap' }}>
          <span>Invoiced: <strong>{inr(summary.invoiced)}</strong></span>
          <span style={{ color: '#166534' }}>Received: <strong>{inr(summary.received)}</strong></span>
          <span style={{ color: '#A32D2D' }}>Outstanding: <strong>{inr(summary.outstanding)}</strong></span>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['Invoice No', 'GPO', 'Buyer', 'Date', 'Qty', 'Amount', 'GST', 'Total', 'Due', 'Status', 'Balance', ''].map(h => <th key={h} style={thS}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={12} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>No sales invoices yet.</td></tr>
            : rows.map(i => (
              <tr key={i.id} style={{ borderBottom: '1px solid #f5f5f5',
                background: i.voided_at ? '#FBF7F7' : undefined,
                color: i.voided_at ? '#aaa' : undefined,
                textDecoration: i.voided_at ? 'line-through' : undefined }}>
                <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{i.invoice_no}{i.file_url && <a href={fileHref(i.file_url)} target="_blank" rel="noreferrer" style={{ marginLeft: 4 }}>📎</a>}</td>
                <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{i.gpo_no || '—'}</td>
                <td style={tdS}>{i.buyer || '—'}</td><td style={tdS}>{fmtD(i.invoice_date)}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{num(i.qty).toLocaleString('en-IN')}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{inr(i.amount)}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{inr(i.gst_amount)}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{inr(i.total_amount)}</td>
                <td style={{ ...tdS, color: (i.buyer_due_date || i.due_date) && new Date(i.buyer_due_date || i.due_date) < new Date() && i.payment_status !== 'paid' ? '#A32D2D' : '#333', fontWeight: 600 }}>{i.is_legacy === false && !i.buyer_due_date ? <span style={{ color: '#6B7280', fontWeight: 500 }}>awaiting GRC</span> : fmtD(i.buyer_due_date || i.due_date)}</td>
                <td style={tdS}>{payTag(i.payment_status)}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: num(i.balance) > 0 ? '#A32D2D' : '#166534' }}>{inr(i.balance)}</td>
                <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                  {i.payment_status !== 'paid' && <button onClick={() => setPayInv(i)} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: '#166534', marginRight: 4 }}>₹ Payment</button>}
                  {/* Shown on a locked invoice too (3 Oct 2026): the modal says WHICH lock and why, instead of
                      the button silently vanishing and leaving the user hunting for a control that is not there. */}
                  {canAdjust && (
                    <button onClick={() => setEditInv(i)} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: salesInvLock(i) ? '#9CA3AF' : '#3B3A8F' }}
                      title={salesInvLock(i) ? salesInvLock(i).short : undefined}>{salesInvLock(i) ? '🔒 Edit' : 'Edit'}</button>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {aging && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Aging Analysis (outstanding balances)</div>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 560 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>{['Awaiting GRC', 'Not due', '0–30 days', '31–60 days', '61–90 days', '>90 days'].map(h => <th key={h} style={thS}>{h}</th>)}</tr></thead>
            <tbody><tr>
              {['awaiting_grc', 'not_due', '0_30', '31_60', '61_90', '90_plus'].map((k, i) => (
                <td key={k} style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: ['#6B7280', '#0F7B3F', '#9A6B00', '#C2410C', '#A32D2D', '#7f1d1d'][i] }}>{inr(aging[k] || 0)}</td>
              ))}
            </tr></tbody>
          </table>
        </div>
      )}
      {showNew && <NewSalesInvModal gpos={gpos} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {payInv && <InvoicePaymentModal inv={payInv} kind="sales" canReverse={canAdjust} onClose={() => setPayInv(null)} onChanged={load} />}
      {editInv && <SalesInvEditModal inv={editInv} gpos={gpos} onClose={() => setEditInv(null)} onSaved={() => { setEditInv(null); load(); }} />}
    </div>
  );
}

// Sales payment modal — amount (>0, <= outstanding), UTR, date (not future), bank credited.
// Shows the payment ledger with per-payment reversal (mandatory reason; never deleted).
// ONE modal for both sides of the ledger (mig 286). The payable side used to have a separate
// `PaymentModal` that posted an amount and showed nothing back, because there was no GET behind
// it. Now that purchase invoices have a real payment ledger the two screens are the same screen,
// and the only differences are the endpoint and whether the money is coming in or going out.
// `kind` is 'sales' or 'purchase'.
function InvoicePaymentModal({ inv, kind = 'sales', canReverse, onClose, onChanged }) {
  const isPurchase = kind === 'purchase';
  const base = isPurchase ? 'purchase-invoices' : 'sales-invoices';
  const L = isPurchase
    ? { title: 'Record Payment', amount: 'Amount paid \u20b9 *', bank: 'Bank debited *', ref: 'UTR / cheque no *', pick: 'Select the bank account debited',
        note: 'Settled against the balance \u2014 the claim net of its debit note, not the invoice total.' }
    : { title: 'Record Payment', amount: 'Amount received \u20b9 *', bank: 'Bank credited *', ref: 'UTR / reference *', pick: 'Select the bank account credited',
        note: '' };
  const [banks, setBanks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState({ payment_date: today(), amount: '', reference: '', bank_account_id: '' });
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const load = useCallback(() => api.get(`/finance/${base}/${inv.id}/payments`).then(r => setPayments(r.data.data || [])).catch(() => {}), [inv.id, base]);
  useEffect(() => { load(); api.get('/finance/bank-accounts').then(r => setBanks(r.data.data || [])).catch(() => {}); }, [load]);
  const outstanding = num(inv.balance);
  const save = async () => {
    setError('');
    if (!(num(form.amount) > 0)) return setError('Amount must be greater than 0');
    if (num(form.amount) > outstanding + 0.01) return setError(`Amount exceeds outstanding ${inr(outstanding)}`);
    if (!form.reference.trim()) return setError('UTR / reference is required');
    if (!form.bank_account_id) return setError(L.pick);
    if (form.payment_date > today()) return setError('Payment date cannot be in the future');
    setBusy(true);
    try { await api.post(`/finance/${base}/${inv.id}/payment`, form); onChanged(); onClose(); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  const reverse = async (p) => {
    const reason = window.prompt(`Reverse payment ${inr(p.amount)} (${p.reference})?\nMandatory reason:`, '');
    if (reason === null) return;
    if (!reason.trim()) { alert('Reason is required'); return; }
    try { await api.post(`/finance/${base}/${inv.id}/payments/${p.id}/reverse`, { reason: reason.trim() }); load(); onChanged(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2100, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 560, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>{L.title} — {inv.invoice_no}</h3></div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#888' }}>Outstanding balance: <strong>{inr(outstanding)}</strong>{L.note && <><br /><span style={{ fontSize: 11 }}>{L.note}</span></>}</p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {/* DOOR B IS CLOSED FOR VENDOR BILLS (Rajan, 3 Oct 2026): a bill is paid through a release
            request — raised, approved, then paid from Requests → To pay. This modal keeps the bill's
            payment LEDGER and its reversal; it no longer records a payment against a purchase bill. */}
        {isPurchase ? (
          <div style={{ background: '#F6FAFF', border: '1px solid #C4DAF2', borderRadius: 6, padding: '9px 12px', fontSize: 12, color: '#1D4E89' }}>
            Vendor bills are paid through a release request: <strong>Raise release</strong> on the bill, it is approved, then paid from <strong>Requests → To pay</strong>.
          </div>
        ) : (<>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{L.amount}</label><input type="number" step="0.01" style={{ ...inputStyle, width: '100%' }} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
          <div><label style={labelStyle}>Payment date * (not future)</label><input type="date" max={today()} style={{ ...inputStyle, width: '100%' }} value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} /></div>
          <div><label style={labelStyle}>{L.ref}</label><input style={{ ...inputStyle, width: '100%' }} value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} /></div>
          <div><label style={labelStyle}>{L.bank}</label>
            <select style={{ ...inputStyle, width: '100%' }} value={form.bank_account_id} onChange={e => setForm({ ...form, bank_account_id: e.target.value })}>
              <option value="">— bank —</option>{banks.map(b => <option key={b.id} value={b.id}>{b.bank_name} · {b.bank_code}{b.account_number ? ' · ' + b.account_number : ''}</option>)}</select></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Close</button>
          <button disabled={busy} onClick={save} style={{ ...btnPrimary, background: '#166534' }}>{busy ? '…' : '₹ Record'}</button>
        </div>
        </>)}
        {isPurchase && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Close</button></div>}
        {payments.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', marginBottom: 6 }}>Payment ledger</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['Date', 'Amount', 'UTR', 'Bank', 'Status', ''].map(h => <th key={h} style={thS}>{h}</th>)}</tr></thead>
              <tbody>{payments.map(p => (
                <tr key={p.id} style={{ opacity: p.reversed ? 0.55 : 1 }}>
                  <td style={tdS}>{fmtD(p.payment_date)}</td><td style={{ ...tdS, textAlign: 'right' }}>{inr(p.amount)}</td>
                  <td style={tdS}>{p.reference}</td><td style={tdS}>{p.bank_name || '—'}</td>
                  <td style={tdS}>{p.reversed ? <span style={{ color: '#A32D2D', fontWeight: 700 }}>reversed</span> : <span style={{ color: '#166534' }}>active</span>}</td>
                  <td style={tdS}>{canReverse && !p.reversed && <button onClick={() => reverse(p)} style={{ ...btnPrimary, padding: '3px 8px', fontSize: 10, background: '#A32D2D' }}>Reverse</button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Sales invoice edit — accounts/admin, only while unlocked (backend is the absolute authority).
// The four absolute locks of PATCH /finance/sales-invoices/:id, in the server's order and words. The
// server remains the authority; this only lets the modal SAY the lock before anyone types a reason.
function salesInvLock(inv) {
  if (num(inv.amount_received) > 0) return { short: 'Locked: payment received', text: 'Payment received — this invoice is locked (Lock A). No override, admin included.' };
  if (inv.discounted_on) return { short: 'Locked: discounted', text: 'Invoice discounted — locked (Lock B). No override, admin included.' };
  if (inv.has_credit_note) return { short: 'Locked: credit note', text: 'A credit note exists against this invoice — locked (Lock C). No override, admin included.' };
  if (inv.review_status === 'approved') return { short: 'Locked: certified', text: 'This invoice is certified — locked (Lock D). Nothing can be changed or attached. If it is wrong, reject the certification first; that is a recorded act.' };
  return null;
}

function SalesInvEditModal({ inv, gpos, onClose, onSaved }) {
  const lock = salesInvLock(inv);
  const [form, setForm] = useState({
    file_url: inv.file_url || '',
    qty: inv.qty ?? '', rate_per_pc: inv.rate_per_pc ?? '', gst_pct: inv.gst_pct ?? '',
    style_no: inv.style_no || '', garment_desc: inv.garment_desc || '',
    invoice_date: (inv.invoice_date || '').slice(0, 10), shipment_date: (inv.shipment_date || '').slice(0, 10),
    busy_invoice_no: inv.busy_invoice_no || '',
    buyer_id: inv.buyer_id || '', brand_id: inv.brand_id || '', reason: '',
  });
  const [buyers, setBuyers] = useState([]); const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  useEffect(() => {
    api.get('/buyers').then(r => setBuyers((r.data.data || []).filter(b => b.deleted_at == null))).catch(() => {});
    api.get('/buyers/brands').then(r => setBrands(r.data.data || [])).catch(() => {});
  }, []);
  const brandsFor = (bid) => brands.filter(b => b.buyer_id === bid);
  // THE DISPATCH IS READ-ONLY HERE (ruled 3 Oct 2026): moving it detaches the invoice from the goods it
  // bills, and the server refuses any change to it. The buyer follows the dispatch, so on a dispatched
  // invoice the buyer is fixed too — changing it used to clear the dispatch as a side effect.
  const dispatched = !!inv.dispatch_id;
  const save = async () => {
    setError('');
    if (!form.reason.trim()) return setError('Reason is required');
    setBusy(true);
    try { await api.patch(`/finance/sales-invoices/${inv.id}`, form); onSaved(); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2100, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 560, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Edit — {inv.invoice_no}</h3></div>
        <p style={{ margin: '0 0 12px', fontSize: 11, color: '#888' }}>Invoice no is not editable. Locks: any payment, discounting, a credit note, or certification — all absolute.</p>
        {lock && <div style={{ background: '#FEF3C7', color: '#92400E', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12, fontWeight: 600 }}>🔒 {lock.text}</div>}
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>Buyer</label>
            <select style={{ ...inputStyle, width: '100%' }} value={form.buyer_id} onChange={e => setForm({ ...form, buyer_id: e.target.value, brand_id: '' })} disabled={dispatched}
              title={dispatched ? 'The buyer comes with the dispatch this invoice bills' : undefined}>
              <option value="">—</option>{buyers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
          <div><label style={labelStyle}>Brand</label>
            <select style={{ ...inputStyle, width: '100%' }} value={form.brand_id} onChange={e => setForm({ ...form, brand_id: e.target.value })} disabled={!form.buyer_id}>
              <option value="">—</option>{brandsFor(form.buyer_id).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
          <div><label style={labelStyle}>Consignment (dispatch)</label>
            <div style={{ ...inputStyle, width: '100%', background: '#F4F4F8', color: dispatched ? '#333' : '#888' }}>
              {dispatched ? (inv.dispatch_dc_no || 'Dispatch on record') : 'None — not attached to a dispatch'}</div>
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Not editable — the dispatch is the goods this invoice bills.</div></div>
          <div><label style={labelStyle}>Busy invoice no</label><input style={{ ...inputStyle, width: '100%' }} value={form.busy_invoice_no} onChange={e => setForm({ ...form, busy_invoice_no: e.target.value })} /></div>
          {[['style_no', 'Style'], ['garment_desc', 'Description']].map(([k, l]) => (
            <div key={k}><label style={labelStyle}>{l}</label><input style={{ ...inputStyle, width: '100%' }} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
          ))}
          {[['invoice_date', 'Invoice Date', 'date'], ['shipment_date', 'Shipment Date', 'date'], ['qty', 'Qty', 'number'], ['rate_per_pc', 'Rate/pc ₹', 'number'], ['gst_pct', 'GST %', 'number']].map(([k, l, t]) => (
            <div key={k}><label style={labelStyle}>{l}</label><input type={t} style={{ ...inputStyle, width: '100%' }} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
          ))}
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Invoice PDF</label>
            {inv.file_url && <a href={fileHref(inv.file_url)} target="_blank" rel="noreferrer" style={{ fontSize: 12, marginRight: 10 }}>📎 current file</a>}
            {lock ? <span style={{ fontSize: 12, color: '#92400E' }}>Cannot attach: {lock.short.replace('Locked: ', '')}.</span>
              : <FileBtn label={form.file_url && form.file_url !== (inv.file_url || '') ? '✅ new file attached — save to keep it' : (inv.file_url ? '📎 Replace invoice PDF' : '📎 Upload invoice PDF')} onUploaded={(u) => setForm({ ...form, file_url: u })} />}
            {inv.file_url && !lock && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Replacing keeps the old file: it stays on the server and the edit history records which file it was.</div>}</div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Reason * (required)</label>
            <textarea rows={2} disabled={!!lock} style={{ ...inputStyle, width: '100%', resize: 'vertical' }} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Why is this correction being made?" /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>{lock ? 'Close' : 'Cancel'}</button>
          {!lock && <button disabled={busy || !form.reason.trim()} onClick={save} style={btnPrimary}>{busy ? '…' : '💾 Save changes'}</button>}
        </div>
      </div>
    </div>
  );
}

// Record a sale AND the shipment it bills, in one act (2026-09-01).
//
// This used to be a bare invoice form with a mandatory "Consignment (dispatch)" dropdown that
// Accounts could never fill: dispatches are created in Order Execution under roles they do not
// hold, so the only dispatch-shaped control on their screen demanded a document they had no
// authority to create. The form now writes the dispatch itself, server-side, in the same
// transaction — POST /finance/sales-invoices/with-dispatch.
//
// The operator types what the system cannot know: how many pieces went, and the day they went.
// Buyer, rate, style and description are resolved from the GPO.
function NewSalesInvModal({ gpos, onClose, onSaved }) {
  const [form, setForm] = useState({ invoice_date: today(), dispatch_date: today(), gst_pct: 5 });
  const [ctx, setCtx] = useState(null);          // buyer / rate / ordered / already-invoiced, from the GPO
  const [ctxErr, setCtxErr] = useState('');
  const [transporters, setTransporters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [warn, setWarn] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get('/finance/transporters').then(r => setTransporters(r.data.data || [])).catch(() => {});
  }, []);

  // The buyer and the rate live on the ORDER, which the GPO list does not carry — so this is a
  // fetch, not a lookup in `gpos`.
  const onGpo = (gpoNo) => {
    setForm(f => ({ ...f, gpo_no: gpoNo }));
    setCtx(null); setCtxErr('');
    if (!gpoNo) return;
    api.get('/finance/sales-invoices/gpo-context?gpo_no=' + encodeURIComponent(gpoNo))
      .then(r => {
        const c = r.data.data;
        setCtx(c);
        setForm(f => ({
          ...f,
          style_no: c.style_no || f.style_no,
          garment_desc: c.item_desc || f.garment_desc,
          // Prefill only — the rate a shipment actually billed at can differ from the order's.
          rate_per_pc: f.rate_per_pc ?? (c.fob_price ?? ''),
        }));
      })
      .catch(e => setCtxErr(e.response?.data?.error || 'Could not read that GPO.'));
  };

  const amount = num(form.qty) * num(form.rate_per_pc);
  const gst = amount * num(form.gst_pct) / 100;
  // Shown before the save, not reported after it: the 110% cap does not bind on this path.
  const overPct = ctx && num(ctx.order_qty) > 0
    ? (num(ctx.invoiced_qty) + num(form.qty)) / num(ctx.order_qty) * 100 : null;

  const save = async () => {
    setError(''); setWarn('');
    if (!form.gpo_no) return setError('GPO is required — without it the invoice cannot reach margin or profitability.');
    if (!ctx?.buyer_id) return setError('That GPO does not resolve to a buyer — the invoice would have no party.');
    if (!num(form.qty) || !num(form.rate_per_pc)) return setError('Quantity and rate per piece are required.');
    if (!form.dispatch_date) return setError('The dispatch date is required — it is the day the goods went.');
    if (form.dispatch_date > today()) return setError('The dispatch date cannot be in the future — this records a shipment that has already gone.');
    setBusy(true);
    try {
      const r = await api.post('/finance/sales-invoices/with-dispatch', form);
      const d = r.data.data;
      setDone({ invoice_no: d.invoice_no, dc_no: d.dc_no });
      if (r.data.warning) setWarn(r.data.warning);
      setTimeout(onSaved, r.data.warning ? 3000 : 1400);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 560, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>＋ Record a Sale <span style={{ fontSize: 11, color: '#bbb' }}>(invoice + delivery challan, auto-numbered)</span></h3>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 11, color: '#888' }}>
          For a shipment that has already gone out. This writes the delivery challan and the invoice together.
          Goods still to be packed should go through Order Execution → Dispatch instead, so they pass the ASN check.
        </p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {done && <div style={{ background: '#E9F7EF', color: '#0F7B3F', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>✅ Saved — invoice <b>{done.invoice_no}</b> against challan <b>{done.dc_no}</b>.</div>}
        {warn && <div style={{ background: '#FFF4E5', color: '#8A6D00', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>⚠ {warn}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>GPO *</label>
            <select style={{ ...inputStyle, width: '100%' }} value={form.gpo_no || ''} onChange={e => onGpo(e.target.value)}>
              <option value="">— select the order this sale belongs to —</option>
              {gpos.map(g => <option key={g.id} value={g.gpo_no}>{g.gpo_no} · {g.style_no || ''}</option>)}
            </select>
            {ctxErr && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4 }}>{ctxErr}</div>}
          </div>
          {ctx && (
            <div style={{ gridColumn: '1 / -1', background: '#F7F6F3', borderRadius: 8, padding: '8px 12px', fontSize: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span>Buyer: <strong>{ctx.buyer_name || '—'}</strong></span>
              <span>Ordered: <strong>{num(ctx.order_qty).toLocaleString('en-IN')}</strong></span>
              <span>Already invoiced: <strong>{num(ctx.invoiced_qty).toLocaleString('en-IN')}</strong></span>
              {overPct != null && overPct > 100 && <span style={{ color: '#8A6D00', fontWeight: 700 }}>⚠ this save takes the GPO to {overPct.toFixed(1)}%</span>}
            </div>
          )}
          <div><label style={labelStyle}>Dispatch date * <span style={{ color: '#aaa', fontWeight: 400 }}>(the day it went)</span></label>
            <input type="date" max={today()} style={{ ...inputStyle, width: '100%' }} value={form.dispatch_date || ''} onChange={e => setForm({ ...form, dispatch_date: e.target.value })} /></div>
          <div><label style={labelStyle}>Invoice date</label>
            <input type="date" style={{ ...inputStyle, width: '100%' }} value={form.invoice_date || ''} onChange={e => setForm({ ...form, invoice_date: e.target.value })} /></div>
          <div><label style={labelStyle}>Qty (pieces) *</label>
            <input type="number" style={{ ...inputStyle, width: '100%' }} value={form.qty ?? ''} onChange={e => setForm({ ...form, qty: e.target.value })} /></div>
          <div><label style={labelStyle}>Rate/pc ₹ * {ctx?.fob_price != null && <span style={{ color: '#aaa', fontWeight: 400 }}>(order: {inr(ctx.fob_price)})</span>}</label>
            <input type="number" style={{ ...inputStyle, width: '100%' }} value={form.rate_per_pc ?? ''} onChange={e => setForm({ ...form, rate_per_pc: e.target.value })} /></div>
          <div><label style={labelStyle}>GST %</label>
            <input type="number" style={{ ...inputStyle, width: '100%' }} value={form.gst_pct ?? ''} onChange={e => setForm({ ...form, gst_pct: e.target.value })} /></div>
          <div><label style={labelStyle}>Busy invoice no</label>
            <input style={{ ...inputStyle, width: '100%' }} value={form.busy_invoice_no || ''} onChange={e => setForm({ ...form, busy_invoice_no: e.target.value })} /></div>
          {[['style_no', 'Style'], ['garment_desc', 'Description']].map(([k, l]) => (
            <div key={k}><label style={labelStyle}>{l}</label>
              <input style={{ ...inputStyle, width: '100%' }} value={form[k] || ''} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
          ))}
          <div style={{ gridColumn: '1 / -1', fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginTop: 4 }}>Transport (optional)</div>
          <div><label style={labelStyle}>Transporter</label>
            <select style={{ ...inputStyle, width: '100%' }} value={form.transporter_id || ''}
              onChange={e => setForm({ ...form, transporter_id: e.target.value, transporter: transporters.find(t => t.id === e.target.value)?.name || null })}>
              <option value="">— none —</option>{transporters.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
          <div><label style={labelStyle}>Vehicle no</label>
            <input style={{ ...inputStyle, width: '100%' }} value={form.vehicle_no || ''} onChange={e => setForm({ ...form, vehicle_no: e.target.value })} /></div>
          <div><label style={labelStyle}>LR no</label>
            <input style={{ ...inputStyle, width: '100%' }} value={form.lr_no || ''} onChange={e => setForm({ ...form, lr_no: e.target.value })} /></div>
          <div><label style={labelStyle}>Destination</label>
            <input style={{ ...inputStyle, width: '100%' }} value={form.destination || ''} onChange={e => setForm({ ...form, destination: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Remarks</label>
            <input style={{ ...inputStyle, width: '100%' }} value={form.remarks || ''} onChange={e => setForm({ ...form, remarks: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1', background: '#F7F6F3', borderRadius: 8, padding: '8px 12px', fontSize: 12, display: 'flex', gap: 16 }}>
            <span>Amount: <strong>{inr(amount)}</strong></span><span>GST: <strong>{inr(gst)}</strong></span>
            <span>Total: <strong style={{ color: '#3B3A8F', fontSize: 14 }}>{inr(amount + gst)}</strong></span></div>
          <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#888' }}>
            Due date is set from the buyer's payment terms. The challan's ETA is the dispatch date — the goods have already travelled.
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <FileBtn label={form.file_url ? '✅ invoice PDF attached' : '📎 Upload invoice PDF'} onUploaded={(u) => setForm({ ...form, file_url: u })} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy || !!done} onClick={save} style={btnPrimary}>{busy ? '…' : '💾 Save sale + challan'}</button>
        </div>
      </div>
    </div>
  );
}


// ═══ Tab 5 — Purchase Invoices ═══

// Every PO a bill covers. purchase_invoices.po_no names only the FIRST one — a vendor
// legitimately bills several POs on one delivery — so the list renders the whole set and
// says how many there are. Server sends po_nos[]; falls back to the header label.
function PoList({ po_nos, po_no }) {
  const list = (po_nos && po_nos.length ? po_nos : (po_no ? [po_no] : []));
  if (!list.length) return <span style={{ color: '#aaa' }}>—</span>;
  return (
    <span style={{ fontFamily: 'monospace', display: 'inline-block', lineHeight: 1.5 }} title={list.join('\n')}>
      {list.map((p, i) => <div key={p + i}>{p}</div>)}
      {list.length > 1 && (
        <span style={{ fontFamily: 'inherit', fontSize: 10, color: '#3B3A8F', background: '#EEF0FA',
          padding: '1px 6px', borderRadius: 10, whiteSpace: 'nowrap' }}>{list.length} POs</span>
      )}
    </span>
  );
}

// Claim (what the vendor billed) vs line value (what arrived, at PO rate). A tax-inclusive
// claim reconciles by itself and reads calm; only an unexplained gap is coloured.
function VarianceCell({ v }) {
  if (!v) return <span style={{ color: '#bbb' }}>—</span>;
  if (v.status === 'no_lines') return <span style={{ color: '#bbb' }}>no lines</span>;
  if (v.status === 'reconciled') return <span style={{ color: '#166534' }} title="Claim equals the goods received at PO rate">✓ {inr(v.line_value)}</span>;
  if (v.status === 'reconciled_tax') return (
    <span style={{ color: '#166534' }} title={`Claim is the tax-inclusive total: lines ${inr(v.line_value)} + ${v.implied_gst_pct}% GST`}>
      ✓ {inr(v.line_value)} <span style={{ color: '#888' }}>+{v.implied_gst_pct}%</span></span>);
  return (
    <span style={{ color: '#A32D2D', fontWeight: 700 }}
      title={`Lines ${inr(v.line_value)} at PO rate · claim exceeds them by ${inr(v.variance)} (${v.variance_pct}%), and that is not a GST slab`}>
      ⚠ {inr(v.line_value)} <span style={{ fontWeight: 400 }}>({v.variance > 0 ? '+' : ''}{inr(v.variance)})</span></span>);
}

// ═══ GRC Certification queue — READY (QC-cleared, GRC previewed) vs AWAITING QC ═══
// GRC queue search — ONE box across five fields, not five boxes.
//
// PADDING, BOTH SIDES. invoice_no is stored zero-padded ("01987") while vendors, statements
// and people quote it unpadded ("1987"). Stripping only the stored side fixes half the
// problem, so both the term and the value are stripped before comparing.
const stripPad = (v) => String(v == null ? '' : v).trim().replace(/^0+(?=\d)/, '');

// RUPEE BAND, not a prefix. "3931" must find 3,931.00 and 3,931.49 — the operator reads the
// rupees off a paper bill and never types the paise. Whole-rupee comparison also stops "3931"
// dragging in 13,931 or 39,310, which a substring match on the formatted string would.
const rupeeMatch = (amount, term) => {
  if (!/^\d+(\.\d+)?$/.test(term)) return false;
  const a = Number(amount);
  return Number.isFinite(a) && Math.floor(a) === Math.floor(Number(term));
};

// EVERY PO on the bill, not just the header's one — a bill spanning four POs must be findable
// by any of them, which is the whole reason PoList exists on the row.
const grcMatches = (inv, raw) => {
  const t = String(raw || '').trim().toLowerCase();
  if (!t) return true;
  if (stripPad(inv.invoice_no).toLowerCase().includes(stripPad(t).toLowerCase())) return true;
  if ((inv.po_nos || []).some(po => String(po || '').toLowerCase().includes(t))) return true;
  // gpo_no now arrives on the payload (finance.routes.js certification-queue SELECT).
  if (inv.gpo_no && String(inv.gpo_no).toLowerCase().includes(t)) return true;
  if ((inv.lines || []).some(l => l.gpo_no && String(l.gpo_no).toLowerCase().includes(t))) return true;
  // SUPPLIER. Two live invoices are both numbered 348 — FINE LABELS at Rs 3,246 and SHIVAM
  // at Rs 690 — and without this nothing typeable separates them. Substring, so a fragment
  // of the trading name is enough; the free-text supplier column is all there is until
  // payee_id adoption gives these rows a real vendor identity.
  if (String(inv.supplier || '').toLowerCase().includes(t)) return true;
  if (rupeeMatch(inv.invoice_amount, t)) return true;
  if (rupeeMatch(inv.debit_total, t)) return true;
  return false;
};

function GrcCertificationTab() {
  // ── WHAT THIS QUEUE IS FOR (reworked 2 Oct 2026) ─────────────────────────────────────────────
  // Accounts certifies vendor bills here against the ORIGINAL documents. So the screen puts the PO
  // and the GRN beside each bill, sorts by SUPPLIER so one vendor's paperwork is done in one sitting
  // (value and age offered too), and has a THIRD VERDICT beside certify and reject: HOLD (mig 352).
  //
  // "Pending" meant both "nobody has looked" and "looked, could not verify" — the second was
  // recorded on WhatsApp. A hold is an act with a reason and a name; the bill stays pending, moves
  // to the Held tab, and returns to the queue on release. The list SHRINKS only when a bill is
  // dealt with: certified or rejected leaves the queue; held moves to its own tab, still counted.
  const [data, setData] = useState({ ready: [], awaiting_qc: [] });
  const [sub, setSub] = useState('ready');
  const [open, setOpen] = useState(null);      // expanded invoice_id
  const [q, setQ] = useState('');              // one search box, filters the ACTIVE tab only
  const [sort, setSort] = useState('supplier'); // supplier | value | age
  const [busy, setBusy] = useState(null);      // acting-on invoice_id
  const [dnotes, setDnotes] = useState([]);    // debit-notes list
  const load = useCallback(async () => {
    // A FAILED LOAD IS NOT AN EMPTY QUEUE. It used to fall back to [] silently, which reads as
    // "Nothing awaiting certification" — the one message that sends Accounts home.
    try { const r = await api.get('/finance/certification-queue'); setData({ ...(r.data.data || { ready: [], awaiting_qc: [] }), error: null }); }
    catch (e) { setData({ ready: [], awaiting_qc: [], error: e.response?.data?.error || 'The queue could not be loaded — reload, or tell IT if it persists.' }); }
  }, []);
  const loadDnotes = useCallback(async () => {
    const r = await api.get('/finance/debit-notes').catch(() => ({ data: { data: [] } }));
    setDnotes(r.data.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (sub === 'debitnotes') loadDnotes(); }, [sub, loadDnotes]);

  const post = (inv, body) => api.post(`/finance/purchase-invoices/${inv.invoice_id}/certify`, body || {});
  const done = (r) => { setBusy(null); setOpen(null);
    alert(`Certified. GRC ${inr(r.data.data.grc_amount)}` + (r.data.data.debit_note_no ? ` · debit note ${r.data.data.debit_note_no}` : ' · no debit')); load(); };

  const certify = async (inv) => {
    const v = inv.variance;
    const warn = v && v.status === 'variance'
      ? `\n\n⚠ The vendor claims ${inr(v.claim)} but the goods received are worth ${inr(v.line_value)} at PO rate — a difference of ${inr(v.variance)} (${v.variance_pct}%).`
      : '';
    const pos = (inv.po_nos || []).length > 1 ? `\nCovers ${inv.po_nos.length} POs: ${inv.po_nos.join(', ')}` : '';
    if (!window.confirm(`Certify ${inv.invoice_no}?\nGRC (payable) = ${inr(inv.grc)}  ·  debit note ${inr(inv.debit_total)}${pos}${warn}\nThis is final and cannot be undone.`)) return;
    setBusy(inv.invoice_id);
    try { done(await post(inv)); }
    catch (e) {
      // 409 + variance = the claim-vs-lines gate. Release it only with a typed reason, which
      // the server records on the invoice. Never auto-acknowledge.
      const err = e.response?.data;
      if (e.response?.status === 409 && err?.variance?.status === 'variance') {
        const reason = askReason(`${err.error}\n\nTo certify anyway, type the reason (required):`);
        if (reason == null) { setBusy(null); return; }
        try { done(await post(inv, { acknowledge_variance: true, variance_reason: reason })); }
        catch (e2) { setBusy(null); alert(e2.response?.data?.error || 'Certify failed'); }
        return;
      }
      setBusy(null); alert(err?.error || 'Certify failed');
    }
  };

  // Soft-reject a bad pending invoice (over-billed / wrong / duplicate). Keeps the row + trail;
  // reverses nothing (pending isn't a payable). Finance-only (backend CERT-guarded).
  const rejectInvoice = async (inv) => {
    const reason = askReason(`Reject invoice ${inv.invoice_no}?\nThis voids it (kept with a rejected trail, not deleted) and drops it off the queue.\n\nReason (required):`);
    if (reason == null) return;
    setBusy(inv.invoice_id);
    try { await api.post(`/finance/purchase-invoices/${inv.invoice_id}/reject`, { rejection_reason: reason }); setBusy(null); setOpen(null);
      alert(`Invoice ${inv.invoice_no} rejected.`); load(); }
    catch (e) { setBusy(null); alert(e.response?.data?.error || 'Reject failed'); }
  };

  // HOLD — "looked, could not verify". A reason and the actor, both enforced by the database.
  const holdInvoice = async (inv) => {
    const reason = askReason(`Hold invoice ${inv.invoice_no}?\nIt stays pending, moves to the Held tab, and comes back when released.\n\nWhat could not be verified? (required — e.g. "GRN copy not with the bill", "rate differs from PO, asked vendor")`);
    if (reason == null) return;
    setBusy(inv.invoice_id);
    try { await api.post(`/finance/purchase-invoices/${inv.invoice_id}/hold`, { reason }); setBusy(null); setOpen(null); load(); }
    catch (e) { setBusy(null); alert(e.response?.data?.error || 'Hold failed'); }
  };
  const releaseInvoice = async (inv) => {
    const reason = askReason(`Release the hold on ${inv.invoice_no}?\nHeld by ${inv.hold?.by || '—'}: "${inv.hold?.reason || ''}"\n\nWhat changed? (required — e.g. "GRN copy received")`);
    if (reason == null) return;
    setBusy(inv.invoice_id);
    try { await api.post(`/finance/purchase-invoices/${inv.invoice_id}/release`, { reason }); setBusy(null); setOpen(null); load(); }
    catch (e) { setBusy(null); alert(e.response?.data?.error || 'Release failed'); }
  };

  const ready = data.ready || [], awaiting = data.awaiting_qc || [];
  const readyOpen = ready.filter(i => !i.hold), awaitingOpen = awaiting.filter(i => !i.hold);
  const held = [...ready, ...awaiting].filter(i => i.hold);
  const total = ready.length + awaiting.length;
  const tabCounts = [readyOpen.length, awaitingOpen.length, held.length];
  if (sub === 'debitnotes') {
    return <DebitNotesView dnotes={dnotes} reload={loadDnotes} tabs={tabCounts} sub={sub} setSub={setSub} />;
  }
  // The ACTIVE tab only. Tab counts stay at their full totals deliberately — the "x of y
  // shown" line below carries the filtering, so a tab never lies about what it holds.
  const full = sub === 'ready' ? readyOpen : sub === 'held' ? held : awaitingOpen;
  const filtered = q.trim() ? full.filter(inv => grcMatches(inv, q)) : full;
  const supKey = (i) => String(i.supplier || '').trim().toLowerCase();
  const list = [...filtered].sort(
    sort === 'value' ? (a, b) => num(b.invoice_amount) - num(a.invoice_amount)
    : sort === 'age' ? (a, b) => String(a.invoice_date || '').localeCompare(String(b.invoice_date || ''))
    : (a, b) => supKey(a).localeCompare(supKey(b)) || String(a.invoice_date || '').localeCompare(String(b.invoice_date || '')));
  // Per-supplier totals, for the group header rows when sorted by supplier.
  const groups = {};
  for (const i of list) { const k = supKey(i); (groups[k] = groups[k] || { n: 0, amt: 0 }); groups[k].n++; groups[k].amt += num(i.invoice_amount); }
  const COLS = sub === 'ready' ? 12 : sub === 'held' ? 9 : 8;
  // THE PO's VENDOR beside the bill's party. Every capture path writes pi.supplier from the
  // master, so there is no raw typed vendor name on a purchase bill; the comparison that exists is
  // bill party vs PO vendor. Measured 2 Oct: 1 of 132 differs — FINE THREADS vs fine thread.
  const vendorMismatch = (inv) => {
    if (!inv.po_vendors) return null;
    const vs = inv.po_vendors.split(' | ');
    return vs.some(v => v.trim().toLowerCase() === supKey(inv)) ? null : inv.po_vendors;
  };
  const ageDays = (d) => { if (!d) return null; const p = String(d).slice(0, 10).split('-').map(Number); if (p.length !== 3) return null;
    return Math.floor((Date.now() - Date.UTC(p[0], p[1] - 1, p[2])) / 86400000); };
  const actBtn = (bg) => ({ ...btnPrimary, padding: '5px 10px', fontSize: 11, background: bg, marginLeft: 5 });
  let lastSup = null;
  return (
    <div>
      {data.error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '9px 12px', borderRadius: 8, marginBottom: 10, fontSize: 13, fontWeight: 600 }}>⚠ {data.error}</div>}
      {/* THE QUEUE, HONESTLY. "Pending" is split into what was looked at and held, and what carries no
          recorded examination — two numbers that need different responses. */}
      <div style={{ background: '#F7F6F3', border: '1px solid #E8E7E7', borderRadius: 8, padding: '10px 13px', marginBottom: 12, fontSize: 13, color: '#444' }}>
        <strong>{total}</strong> bills pending certification: <strong style={{ color: '#166534' }}>{readyOpen.length}</strong> ready to certify ·{' '}
        <strong style={{ color: '#9A6B00' }}>{held.length}</strong> examined and held ·{' '}
        <strong style={{ color: '#C2410C' }}>{awaitingOpen.length}</strong> awaiting QC.
        <span style={{ color: '#888' }}> {total - held.length} carry no recorded examination — a hold is the only examination this screen records.</span>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => { setQ(e.target.value); setOpen(null); }}
          placeholder="Search invoice no, PO, GPO or amount…"
          style={{ padding: '8px 11px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, width: 320, boxSizing: 'border-box' }} />
        {q.trim() && (
          <button onClick={() => setQ('')} style={{ border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 12 }}>✕ clear</button>
        )}
        <label style={{ fontSize: 12, color: '#666' }}>Sort{' '}
          <select value={sort} onChange={e => setSort(e.target.value)} style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }}>
            <option value="supplier">by supplier — one vendor's paperwork at a time</option>
            <option value="value">by value — largest first</option>
            <option value="age">by age — oldest bill first</option>
          </select>
        </label>
        {/* ALWAYS rendered, not only while filtering: a short list must never be mistakable
            for a complete one. */}
        <span style={{ fontSize: 12, fontWeight: q.trim() ? 700 : 400,
          color: q.trim() && list.length !== full.length ? '#A32D2D' : '#888' }}>
          {list.length} of {full.length} shown
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {[['ready', `✅ Ready to Certify (${readyOpen.length})`], ['held', `✋ Held (${held.length})`], ['awaiting', `⏳ Awaiting QC (${awaitingOpen.length})`], ['debitnotes', `🧾 Debit Notes`]].map(([k, l]) => (
          <button key={k} onClick={() => { setSub(k); setOpen(null); }} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid ' + (sub === k ? '#3B3A8F' : '#ddd'), cursor: 'pointer',
            background: sub === k ? '#3B3A8F' : 'white', color: sub === k ? 'white' : '#666', fontWeight: sub === k ? 700 : 500, fontSize: 13 }}>{l}</button>
        ))}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {(sub === 'ready'
            ? ['Invoice No', 'PO · GRN', 'Supplier', 'Date', 'Claim ₹', 'vs Lines (at PO rate)', 'Shortage', 'Rejection', 'Delay', 'Debit Note', 'GRC (payable)', '']
            : sub === 'held'
            ? ['Invoice No', 'PO · GRN', 'Supplier', 'Date', 'Claim ₹', 'vs Lines (at PO rate)', 'Held — by, when, why', 'QC', '']
            : ['Invoice No', 'PO · GRN', 'Supplier', 'Date', 'Claim ₹', 'vs Lines (at PO rate)', 'Lines awaiting QC', '']).map(h => <th key={h} style={thS}>{h}</th>)}
        </tr></thead>
        <tbody>
          {list.length === 0 ? <tr><td colSpan={COLS} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>
            {q.trim()
              ? `No invoice matches “${q.trim()}” in this tab.`
              : (sub === 'ready' ? 'Nothing ready to certify.' : sub === 'held' ? 'Nothing held.' : 'No invoices awaiting QC.')}</td></tr>
            : list.map(inv => {
              const sk = supKey(inv);
              const header = sort === 'supplier' && sk !== lastSup;
              lastSup = sk;
              const mism = vendorMismatch(inv);
              const age = ageDays(inv.invoice_date);
              const qcPending = num(inv.pending_qc_count) > 0;
              return (
              <React.Fragment key={inv.invoice_id}>
                {header && (
                  <tr><td colSpan={COLS} style={{ background: '#EEEDF8', padding: '6px 10px', fontWeight: 700, color: '#3B3A8F', fontSize: 12 }}>
                    {inv.supplier || '— no supplier —'} <span style={{ fontWeight: 400, color: '#666' }}>· {groups[sk].n} bill{groups[sk].n > 1 ? 's' : ''} · {inr(groups[sk].amt)}</span>
                  </td></tr>
                )}
                <tr style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }} onClick={() => setOpen(open === inv.invoice_id ? null : inv.invoice_id)}>
                  <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{open === inv.invoice_id ? '▾ ' : '▸ '}{inv.invoice_no}</td>
                  <td style={{ ...tdS, fontSize: 11 }}>
                    <PoList po_nos={inv.po_nos} />
                    {(inv.grn_nos || []).length > 0
                      ? <div style={{ fontFamily: 'monospace', color: '#666', marginTop: 2 }}>{inv.grn_nos.map(g => <div key={g}>{g}</div>)}</div>
                      : <div style={{ color: '#A32D2D', marginTop: 2 }}>no GRN on any line</div>}
                  </td>
                  <td style={tdS}>
                    <div>{inv.supplier || '—'}</div>
                    {mism && <div style={{ fontSize: 11, color: '#9A6B00', fontFamily: 'monospace' }}>⚠ PO vendor is “{mism}”</div>}
                    {!inv.payee_id && <div style={{ fontSize: 11, color: '#9B1C1C' }}>no payee master</div>}
                  </td>
                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{fmtD(inv.invoice_date)}{inv.invoice_date_corrected_from && <div style={{ fontSize: 10, color: '#7A1F1F' }} title="Recorded date correction (mig 387)">corrected from {fmtD(inv.invoice_date_corrected_from)}</div>}{age != null && <div style={{ fontSize: 11, color: age > 30 ? '#A32D2D' : '#999' }}>{age}d old</div>}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{inr(inv.invoice_amount)}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}><VarianceCell v={inv.variance} /></td>
                  {sub === 'ready' ? <>
                    <td style={{ ...tdS, textAlign: 'right', color: inv.shortage ? '#C2410C' : '#bbb' }}>{inr(inv.shortage)}</td>
                    <td style={{ ...tdS, textAlign: 'right', color: inv.rejection ? '#C2410C' : '#bbb' }}>{inr(inv.rejection)}</td>
                    <td style={{ ...tdS, textAlign: 'right', color: inv.delay ? '#C2410C' : '#bbb' }}>{inr(inv.delay)}{inv.delay_days ? ` (${inv.delay_days}d)` : ''}</td>
                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 600 }}>{inr(inv.debit_total)}</td>
                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: num(inv.grc) < 0 ? '#A32D2D' : '#166534' }}>{inr(inv.grc)}</td>
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                      <button disabled={busy === inv.invoice_id} onClick={e => { e.stopPropagation(); certify(inv); }} style={actBtn('#166534')}>{busy === inv.invoice_id ? '…' : '✓ Certify'}</button>
                      <button disabled={busy === inv.invoice_id} onClick={e => { e.stopPropagation(); holdInvoice(inv); }}
                        title="Looked, could not verify it — record why, by name" style={actBtn('#9A6B00')}>✋ Hold</button>
                      <button disabled={busy === inv.invoice_id} onClick={e => { e.stopPropagation(); rejectInvoice(inv); }}
                        title="Void this invoice (kept with a rejected trail, not deleted)" style={actBtn('#A32D2D')}>✕ Reject</button>
                    </td>
                  </> : sub === 'held' ? <>
                    <td style={{ ...tdS, fontSize: 11, maxWidth: 260 }}>
                      <div style={{ color: '#9A6B00', fontWeight: 600 }}>“{inv.hold.reason}”</div>
                      <div style={{ color: '#888' }}>{inv.hold.by || '—'} · {fmtD(inv.hold.at)}</div>
                    </td>
                    <td style={{ ...tdS, fontSize: 11, color: qcPending ? '#C2410C' : '#166534' }}>{qcPending ? `${inv.pending_qc_count} awaiting` : 'cleared'}</td>
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                      {/* Certify is offered only where the QC gate would let it through — never a
                          button the server is certain to refuse. */}
                      {!qcPending && <button disabled={busy === inv.invoice_id} onClick={e => { e.stopPropagation(); certify(inv); }} style={actBtn('#166534')}>✓ Certify</button>}
                      <button disabled={busy === inv.invoice_id} onClick={e => { e.stopPropagation(); releaseInvoice(inv); }}
                        title="Return it to the queue — say what changed" style={actBtn('#3B3A8F')}>↺ Release</button>
                      <button disabled={busy === inv.invoice_id} onClick={e => { e.stopPropagation(); rejectInvoice(inv); }}
                        title="Void this invoice (kept with a rejected trail, not deleted)" style={actBtn('#A32D2D')}>✕ Reject</button>
                    </td>
                  </> : <>
                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: '#C2410C' }}>{inv.pending_qc_count}</td>
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}><span style={{ color: '#aaa', marginRight: 4 }}>QC pending</span>
                      <button disabled={busy === inv.invoice_id} onClick={e => { e.stopPropagation(); holdInvoice(inv); }}
                        title="Looked, could not verify it — record why, by name" style={actBtn('#9A6B00')}>✋ Hold</button>
                      <button disabled={busy === inv.invoice_id} onClick={e => { e.stopPropagation(); rejectInvoice(inv); }}
                        title="Void this invoice (kept with a rejected trail, not deleted)" style={actBtn('#A32D2D')}>✕ Reject</button></td>
                  </>}
                </tr>
                {open === inv.invoice_id && (
                  <tr><td colSpan={COLS} style={{ background: '#FAFAF8', padding: '10px 16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead><tr style={{ color: '#888' }}>
                        {['PO No', 'GRN', 'Item', 'Size', 'Invoiced', 'Received', 'Rejected', 'Rate', 'QC'].map(h => <th key={h} style={{ ...thS, padding: '4px 8px' }}>{h}</th>)}
                      </tr></thead>
                      <tbody>{(inv.lines || []).map((ln, i) => (
                        <tr key={i}>
                          <td style={{ ...tdS, padding: '4px 8px', fontFamily: 'monospace' }}>{ln.po_no || '—'}</td>
                          <td style={{ ...tdS, padding: '4px 8px', fontFamily: 'monospace' }}>{ln.grn_no || '—'}{ln.grn_date ? <span style={{ color: '#999', fontFamily: 'inherit' }}> · {fmtD(ln.grn_date)}</span> : null}</td>
                          <td style={{ ...tdS, padding: '4px 8px' }}>{ln.item || '—'}</td>
                          <td style={{ ...tdS, padding: '4px 8px' }}>{ln.size || '—'}</td>
                          <td style={{ ...tdS, padding: '4px 8px', textAlign: 'right' }}>{num(ln.invoiced_qty)}</td>
                          <td style={{ ...tdS, padding: '4px 8px', textAlign: 'right' }}>{num(ln.received_qty)}
                            {ln.qty_corrected_at && <span title={`Receipt quantity corrected after QC against ${ln.qty_correction_ref || 'a named document'}`}
                              style={{ marginLeft: 5, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 7, background: '#EEEDF8', color: '#3B3A8F' }}>✎ CORRECTED</span>}</td>
                          <td style={{ ...tdS, padding: '4px 8px', textAlign: 'right', color: num(ln.rejected_qty) ? '#C2410C' : '#333' }}>{num(ln.rejected_qty)}</td>
                          <td style={{ ...tdS, padding: '4px 8px', textAlign: 'right' }}>{inr(ln.rate)}</td>
                          <td style={{ ...tdS, padding: '4px 8px' }}>{ln.qc_status === 'pending' ? <span style={{ color: '#C2410C' }}>pending</span> : <span style={{ color: '#166534' }}>{ln.qc_status || '—'}</span>}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                    {/* A corrected receipt reads like a shortage. On a bill that was always for
                        the corrected figure it is the opposite — the RECEIPT was wrong, and once
                        fixed the claim and the goods agree. Say so here, where somebody is about
                        to decide a payable, rather than leaving them to infer it from two
                        columns that now happen to match. */}
                    {(inv.lines || []).some(l => l.qty_corrected_at) && (() => {
                      const corr = (inv.lines || []).filter(l => l.qty_corrected_at);
                      const allMatch = corr.every(l => l.bill_matches_goods);
                      const refs = [...new Set(corr.map(l => l.qty_correction_ref).filter(Boolean))].join(', ');
                      return (
                        <div style={{ marginTop: 8, padding: '7px 11px', borderRadius: 6, fontSize: 11,
                          background: allMatch ? '#EAF7EE' : '#FDF4E7', color: allMatch ? '#166534' : '#8A5A00',
                          border: `1px solid ${allMatch ? '#CDE9D6' : '#F0DDBE'}` }}>
                          ✎ <strong>Receipt corrected after QC</strong>{refs ? ` · read off ${refs}` : ''}.{' '}
                          {allMatch
                            ? 'The bill was already for this quantity — invoiced now matches received, so this correction moves no money. No debit note arises from it.'
                            : 'Invoiced and received still differ on at least one line — check before certifying, the difference is a real claim gap and not an artefact of the correction.'}
                        </div>
                      );
                    })()}
                  </td></tr>
                )}
              </React.Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

// Debit Notes sub-view — vendor-facing print + honest email (relayed ≠ delivered).
function DebitNotesView({ dnotes, reload, tabs, sub, setSub }) {
  const [deliver, setDeliver] = useState(null);
  const [sending, setSending] = useState(null);
  useEffect(() => { api.get('/finance/email-deliverability').then(r => setDeliver(r.data.data)).catch(() => setDeliver(null)); }, []);

  const netOf = (dn) => dn.net_payable != null ? num(dn.net_payable) : num(dn.invoice_amount) - num(dn.debit_note_total);
  const downloadPdf = async (dn) => {
    try {
      const r = await api.get(`/finance/debit-notes/${dn.id}/print`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `${dn.debit_no}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { alert('Could not generate the PDF.'); }
  };
  const copyDn = async (dn) => {
    const txt = `Debit Note ${dn.debit_no}\nDate: ${fmtD(dn.debit_date)}\nVendor: ${dn.supplier || '—'}\nAgainst Invoice: ${dn.invoice_no || '—'}\nInvoice Claim: ${inr(dn.invoice_amount)}\nDebit Note Total: ${inr(dn.debit_note_total)}\nNet Payable: ${inr(netOf(dn))}`;
    try { await navigator.clipboard.writeText(txt); alert('Debit note details copied — you can paste them into an email.'); }
    catch (e) { window.prompt('Copy the debit note details:', txt); }
  };
  const send = async (dn) => {
    if (!window.confirm(`Email debit note ${dn.debit_no} to the vendor?\n\nNote: email delivery is not guaranteed while the sender domain is unverified. The result will show the REAL status (it may say "not delivered").`)) return;
    setSending(dn.id);
    try { const r = await api.post(`/finance/debit-notes/${dn.id}/send`); alert(r.data.data.label + (r.data.data.detail ? `\n\n(${r.data.data.detail})` : '')); reload(); }
    catch (e) { alert(e.response?.data?.error || 'Send failed'); }
    setSending(null);
  };
  const sendBadge = (s) => {
    if (s === 'relayed') return <span style={{ color: '#B45309', fontWeight: 600 }}>⚠ handed to mail server (unverified)</span>;
    if (s === 'failed') return <span style={{ color: '#B91C1C', fontWeight: 600 }}>✕ not delivered</span>;
    return <span style={{ color: '#aaa' }}>— not sent</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[['ready', `✅ Ready to Certify (${tabs[0]})`], ['held', `✋ Held (${tabs[2]})`], ['awaiting', `⏳ Awaiting QC (${tabs[1]})`], ['debitnotes', `🧾 Debit Notes`]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid ' + (sub === k ? '#3B3A8F' : '#ddd'), cursor: 'pointer',
            background: sub === k ? '#3B3A8F' : 'white', color: sub === k ? 'white' : '#666', fontWeight: sub === k ? 700 : 500, fontSize: 13 }}>{l}</button>
        ))}
      </div>
      {deliver && !deliver.deliverable && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12.5, lineHeight: 1.5 }}>
          ⚠️ <strong>Email delivery is currently unavailable</strong> — {(deliver.reasons || []).join('; ') || 'sender domain not configured'} ({deliver.domain}).
          Emails may silently fail to reach vendors. <strong>Print (Download PDF) and send debit notes manually</strong> until DNS is fixed.
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['Debit Note', 'Date', 'Vendor', 'Against Invoice', 'Claim ₹', 'Debit ₹', 'Net Payable ₹', 'Last Email', ''].map(h => <th key={h} style={thS}>{h}</th>)}
        </tr></thead>
        <tbody>
          {dnotes.length === 0 ? <tr><td colSpan={9} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>No debit notes yet — they are minted when Finance certifies an invoice with a deduction.</td></tr>
            : dnotes.map(dn => (
              <tr key={dn.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{dn.debit_no}</td>
                <td style={tdS}>{fmtD(dn.debit_date)}</td>
                <td style={tdS}>{dn.supplier || '—'}</td>
                <td style={{ ...tdS, fontFamily: 'monospace' }}>{dn.invoice_no || '—'}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{inr(dn.invoice_amount)}</td>
                <td style={{ ...tdS, textAlign: 'right', color: '#C2410C', fontWeight: 600 }}>{inr(dn.debit_note_total)}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: netOf(dn) < 0 ? '#A32D2D' : '#166534' }}>{inr(netOf(dn))}</td>
                <td style={{ ...tdS, fontSize: 11 }}>{sendBadge(dn.last_send_status)}</td>
                <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                  <button onClick={() => downloadPdf(dn)} style={{ ...btnPrimary, padding: '4px 9px', fontSize: 10, background: '#3B3A8F' }}>⬇ PDF</button>
                  <button onClick={() => copyDn(dn)} style={{ ...btnPrimary, padding: '4px 9px', fontSize: 10, background: '#6B7280', marginLeft: 5 }}>⧉ Copy</button>
                  <button disabled={sending === dn.id} onClick={() => send(dn)} style={{ ...btnPrimary, padding: '4px 9px', fontSize: 10, background: '#0d9488', marginLeft: 5 }}>{sending === dn.id ? '…' : '✉ Email vendor'}</button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Month-wise expenditure (grouped on invoice_date — the only date populated on every row).
// The uncertified slice sits ALONGSIDE each month's total on purpose: uncertified bills are not
// counted in Total Payable, so a big or growing uncertified column is the tell that certification
// is falling behind and the payable is understating what is actually owed.
const monthLabel = (m) => {
  if (!m) return 'Undated';
  const [y, mo] = m.split('-');
  return new Date(+y, +mo - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

function MonthTable({ months }) {
  const th = { padding: '8px 10px', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap', textAlign: 'right' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap', textAlign: 'right' };
  if (!months.length) return <div style={{ padding: 16, fontSize: 12, color: '#aaa' }}>Nothing in this view.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#FCFCFF' }}>
          {['Month', 'Bills', 'Total invoiced', 'Awaiting certification', 'Bills awaiting'].map((h, i) => (
            <th key={h} style={{ ...th, textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>))}
        </tr></thead>
        <tbody>
          {months.map(m => (
            <tr key={m.month || 'undated'}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: '#2D2C72' }}>{monthLabel(m.month)}</td>
              <td style={td}>{m.bill_count}</td>
              <td style={{ ...td, fontWeight: 800 }}>{inr(m.invoiced)}</td>
              <td style={{ ...td, fontWeight: 800, color: m.uncertified_amount > 0 ? '#A32D2D' : '#999' }}>{inr(m.uncertified_amount)}</td>
              <td style={{ ...td, color: m.uncertified_count > 0 ? '#A32D2D' : '#999' }}>{m.uncertified_count} of {m.bill_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ padding: '8px 12px', fontSize: 11, color: '#777', lineHeight: 1.5 }}>
        {months.filter(m => m.uncertified_amount > 0).slice(0, 3).map(m => (
          <div key={m.month || 'undated'}>
            <strong>{monthLabel(m.month)}:</strong> {inr(m.invoiced)}, of which <strong style={{ color: '#A32D2D' }}>{inr(m.uncertified_amount)} awaiting certification</strong> across {m.uncertified_count} bill{m.uncertified_count === 1 ? '' : 's'}.
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Party-wise totals. Grouped on lower(btrim(supplier)) server-side and NOTHING looser: the
// 21 spellings are 21 distinct vendors ("fine thread" ≠ "FINE LABELS"), so fuzzy merging would
// fuse two real ledgers. Outstanding uses the header KPI's own expression, so with no filters
// applied this column sums to Total Payable.
// ── DUPLICATE PARTIES (the merge queue) ──────────────────────────────────────
// Two rows for one party splits its payment history in half and neither half is wrong, so
// nothing complains. Merging is irreversible in the way that matters — the retired row keeps
// its name and its pointer, but the references have moved — which is why the reason is
// required and why both sides' settled money is shown before anyone commits.
function DuplicateParties({ onDone }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(() => api.get('/finance/pdc/payees/duplicates')
    .then(r => setRows(r.data.data || []))
    .catch(e => setErr(e.response?.data?.error || 'Could not load duplicates')), []);
  useEffect(() => { load(); }, [load]);

  const merge = async (pair, retireA) => {
    const retired = retireA ? { id: pair.a_id, name: pair.a_name } : { id: pair.b_id, name: pair.b_name };
    const keeps   = retireA ? { id: pair.b_id, name: pair.b_name } : { id: pair.a_id, name: pair.a_name };
    const reason = window.prompt(
      `Retire “${retired.name}” into “${keeps.name}”.\n\n`
      + `Every payment, cheque, invoice and day book entry moves to the surviving party, and the `
      + `invoice party name is restamped. The retired row stays, pointing at the survivor.\n\n`
      + `Why are these one party?`);
    if (reason === null) return;
    if (!reason.trim()) { setErr('The reason is the record — it cannot be blank.'); return; }
    setBusy(retired.id); setErr(''); setMsg('');
    try {
      const r = await api.post(`/finance/pdc/payees/${retired.id}/merge`, { into_payee_id: keeps.id, reason: reason.trim() });
      setMsg(r.data.message || 'Merged.'); await load(); onDone?.();
    } catch (e) { setErr(e.response?.data?.error || 'Could not merge'); }
    finally { setBusy(''); }
  };

  if (!rows) return <div style={{ color: '#aaa', fontSize: 13 }}>Loading duplicates…</div>;

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        Parties that look like each other
      </div>
      {msg && <div style={{ background: '#EAF7EE', border: '1px solid #bfe3cb', color: '#166534', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 }}>{msg}</div>}
      {err && <div style={{ background: '#FDECEC', border: '1px solid #f3c2c2', color: '#A32D2D', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      {rows.length === 0
        ? <div style={{ fontSize: 12.5, color: '#888' }}>No two live parties resemble each other.</div>
        : rows.map(pr => {
          // BOTH SIDES SETTLED IS A REFUSAL, and it is shown as one rather than offered and
          // then rejected — which of two paid parties is real is not a merge decision.
          const blocked = pr.a_settled > 0 && pr.b_settled > 0;
          const side = (name, linked, settled) => (
            <div style={{ fontSize: 12.5 }}>
              {name}
              {linked && <span style={{ marginLeft: 6, fontSize: 10.5, background: '#EEF0FA', color: '#3B3A8F', borderRadius: 10, padding: '1px 7px' }}>vendor linked</span>}
              <span style={{ marginLeft: 6, fontSize: 11, color: settled > 0 ? '#A32D2D' : '#888' }}>
                {settled > 0 ? `${settled} settled payment${settled === 1 ? '' : 's'}` : 'nothing settled'}
              </span>
            </div>
          );
          return (
            <div key={pr.a_id + pr.b_id} style={{ border: '1px solid #E8E7E7', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 220 }}>{side(pr.a_name, pr.a_linked, pr.a_settled)}</div>
                <div style={{ flex: 1, minWidth: 220 }}>{side(pr.b_name, pr.b_linked, pr.b_settled)}</div>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: pr.sim >= 1 ? '#166534' : '#B45309' }}>
                  {pr.sim >= 1 ? 'same but for the legal suffix' : `${Math.round(pr.sim * 100)}% alike`}
                </span>
              </div>
              {blocked
                ? <div style={{ fontSize: 11.5, color: '#A32D2D', marginTop: 6 }}>
                    Money has been paid to both. Which one is the real party is a decision about the world, not a merge.
                  </div>
                : <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {/* Named in full both ways round. "Merge" with no direction is how the
                        wrong row survives — the button says which name disappears. */}
                    <button disabled={!!busy} onClick={() => merge(pr, true)}
                      style={{ padding: '4px 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                      Keep “{pr.b_name}”
                    </button>
                    <button disabled={!!busy} onClick={() => merge(pr, false)}
                      style={{ padding: '4px 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                      Keep “{pr.a_name}”
                    </button>
                  </div>}
            </div>
          );
        })}
    </div>
  );
}

// ── PARTY ↔ VENDOR (the confirm queue) ───────────────────────────────────────
// The payee master and the vendor register hold the same party twice. supplier_id is the join
// and almost nothing fills it, so a party typed here and a vendor registered there stay two
// identities with the same name. This is where a person says they are one.
//
// SUGGESTS, NEVER DECIDES. Zero unlinked parties match a vendor by name; every candidate below
// differs in spelling, and the last time a trigram score was allowed to CHOOSE (mig 198) it
// handed two pairs to the malformed name. So the score is shown, and a human clicks.
//
// Most rows here will never have a candidate and that is correct — a painter, a landlord and an
// employee reimbursement are parties, not vendors. The queue is not a list of things to fix.
function PartyVendorLink() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [onlyCandidates, setOnlyCandidates] = useState(true);

  const load = useCallback(() => api.get('/finance/pdc/payees/unlinked')
    .then(r => setRows(r.data.data || []))
    .catch(e => setErr(e.response?.data?.error || 'Could not load the parties')), []);
  useEffect(() => { load(); }, [load]);

  const link = async (payee, cand) => {
    if (!window.confirm(
      `Confirm that the party "${payee.name}" IS the vendor "${cand.name}"?\n\n`
      + `Nothing moves: no payment, cheque or invoice changes hands, and no name is rewritten. `
      + `This only records that the two are one party.`)) return;
    setBusy(payee.id); setErr(''); setMsg('');
    try {
      const r = await api.patch(`/finance/pdc/payees/${payee.id}/link`, { supplier_id: cand.id });
      setMsg(r.data.message || 'Linked.'); await load();
    } catch (e) { setErr(e.response?.data?.error || 'Could not link that party'); }
    finally { setBusy(''); }
  };

  if (err && !rows) return <div style={{ color: '#A32D2D', fontSize: 13 }}>{err}</div>;
  if (!rows) return <div style={{ color: '#aaa', fontSize: 13 }}>Loading…</div>;
  const withCand = rows.filter(r => r.candidates.length);
  const shown = onlyCandidates ? withCand : rows;

  return (
    <div>
      <DuplicateParties onDone={load} />
      <div style={{ fontSize: 12, color: '#666', marginBottom: 10, lineHeight: 1.6 }}>
        <strong>{rows.length}</strong> parties have no vendor record behind them. <strong>{withCand.length}</strong> look
        like a registered vendor under a different spelling. The rest are parties that are not vendors —
        staff advances, petty cash, one-off charges — and belong here unlinked.
        <div style={{ marginTop: 6 }}>
          <label style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyCandidates} onChange={e => setOnlyCandidates(e.target.checked)} />
            {' '}Show only the ones with a possible vendor
          </label>
        </div>
      </div>
      {msg && <div style={{ background: '#EAF7EE', border: '1px solid #bfe3cb', color: '#166534', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 }}>{msg}</div>}
      {err && <div style={{ background: '#FDECEC', border: '1px solid #f3c2c2', color: '#A32D2D', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

      {shown.length === 0
        ? <div style={{ fontSize: 12.5, color: '#888' }}>Nothing here needs a decision.</div>
        : shown.map(p => (
          <div key={p.id} style={{ border: '1px solid #E8E7E7', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 13.5 }}>{p.name}</strong>
              {/* WHAT IS ALREADY BEHIND THIS PARTY. A link is a smaller claim than a merge, but it
                  is still a claim about whose history this is, and the weight of it should be on
                  screen before someone clicks — not discoverable afterwards. */}
              <span style={{ fontSize: 11, color: '#888' }}>
                {[[p.requests, 'request'], [p.cheques, 'cheque'], [p.invoices, 'invoice'], [p.daybook, 'day book entry']]
                  .filter(([n]) => n > 0).map(([n, l]) => `${n} ${l}${n === 1 ? '' : 's'}`).join(' · ') || 'nothing raised against it yet'}
              </span>
            </div>
            {p.candidates.length === 0
              ? <div style={{ fontSize: 11.5, color: '#aaa', marginTop: 4 }}>No vendor resembles this name.</div>
              : p.candidates.map(c => (
                <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                  <span style={{ fontSize: 12.5 }}>{c.name}</span>
                  <span style={{ fontSize: 10.5, background: '#F3F4F6', color: '#666', borderRadius: 10, padding: '1px 7px' }}>{c.category || 'uncategorised'}</span>
                  <span style={{ fontSize: 10.5, color: c.sim >= 1 ? '#166534' : '#B45309', fontWeight: 700 }}>
                    {c.sim >= 1 ? 'same name but for the legal suffix' : `${Math.round(c.sim * 100)}% alike`}
                  </span>
                  {/* Said here rather than left to a 409. Under link-not-merge a vendor already
                      held by another party cannot take a second one, and the answer is a merge
                      pass — so the button is not offered at all. */}
                  {c.taken_by
                    ? <span style={{ fontSize: 11, color: '#A32D2D' }}>
                        already held by the party “{c.taken_by}” — these two need merging, not linking
                      </span>
                    : <button disabled={busy === p.id} onClick={() => link(p, c)}
                        style={{ padding: '4px 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #ddd',
                          borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busy === p.id ? 'wait' : 'pointer' }}>
                        {busy === p.id ? 'Linking…' : 'This is the same party'}
                      </button>}
                </div>
              ))}
          </div>
        ))}
    </div>
  );
}

function PartyTable({ parties, onPick }) {
  const th = { padding: '8px 10px', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap', textAlign: 'right' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap', textAlign: 'right' };
  if (!parties.length) return <div style={{ padding: 16, fontSize: 12, color: '#aaa' }}>Nothing in this view.</div>;
  const tot = parties.reduce((s, p) => ({
    purchased: s.purchased + num(p.purchased), paid: s.paid + num(p.paid), outstanding: s.outstanding + num(p.outstanding),
  }), { purchased: 0, paid: 0, outstanding: 0 });
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#FCFCFF' }}>
          {['Supplier', 'Bills', 'Total purchased', 'Total paid', 'Outstanding'].map((h, i) => (
            <th key={h} style={{ ...th, textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>))}
        </tr></thead>
        <tbody>
          {parties.map(p => (
            <tr key={p.payee_id || p.supplier} onClick={() => onPick(p)} title="Filter to this party"
              style={{ cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#F7F6FF')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: '#2D2C72' }}>{p.supplier}</td>
              <td style={td}>{p.bill_count}</td>
              <td style={td}>{inr(p.purchased)}</td>
              <td style={{ ...td, color: '#166534' }}>{inr(p.paid)}</td>
              <td style={{ ...td, fontWeight: 800, color: num(p.outstanding) > 0 ? '#C2410C' : '#999' }}>{inr(p.outstanding)}</td>
            </tr>
          ))}
          <tr style={{ background: '#F7F6F3' }}>
            <td style={{ ...td, textAlign: 'left', fontWeight: 800 }}>Total ({parties.length} parties)</td>
            <td style={td} />
            <td style={{ ...td, fontWeight: 800 }}>{inr(tot.purchased)}</td>
            <td style={{ ...td, fontWeight: 800, color: '#166534' }}>{inr(tot.paid)}</td>
            <td style={{ ...td, fontWeight: 800, color: '#C2410C' }}>{inr(tot.outstanding)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function RollUp({ title, hint, open, onToggle, children }) {
  return (
    <div style={{ marginBottom: 12, border: '1px solid #E7E5F0', borderRadius: 12, overflow: 'hidden' }}>
      <div onClick={onToggle} style={{ fontSize: 12, fontWeight: 800, color: '#2D2C72', padding: '10px 14px', background: '#F5F4FF', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>{open ? '▾' : '▸'}</span><span>{title}</span>
        <span style={{ fontWeight: 500, color: '#777' }}>{hint}</span>
      </div>
      {open && children}
    </div>
  );
}

function PurchaseInvTab({ isMgmt, canDisburse, canRaise, onBook }) {
  const { user } = useAuth();   // the Hold payment action is offered by role; the server refuses a shared login
  const [rows, setRows] = useState([]);
  // RAISE FROM THE BILL (3 Oct 2026). selBills: bills ticked for one supplier; releaseFor: the bills the
  // confirm dialog is open on; overCeil: the report of every bill raised or paid over the 2x ceiling.
  const [selBills, setSelBills] = useState([]);
  const [releaseFor, setReleaseFor] = useState(null);
  const [overCeil, setOverCeil] = useState([]);
  const [openOver, setOpenOver] = useState(false);
  const raisable = (i) => canRaise && i.source === 'invoice' && i.review_status === 'approved' && !i.voided_at && i.payment_status !== 'paid' && !i.hold_kind;
  const supKey = (i) => (i.supplier || '').trim().toLowerCase();
  const [summary, setSummary] = useState(null);
  const [months, setMonths] = useState([]);
  const [parties, setParties] = useState([]);
  const [openMonths, setOpenMonths] = useState(false);
  const [openParties, setOpenParties] = useState(false);
  const [filters, setFilters] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [payInv, setPayInv] = useState(null);
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  // THE COMPANY THIS LIST IS FOR. Without it the tab scoped to the caller's HOME company only —
  // and every Deroi operator's home is RSR, so the payables view built for them showed 395 RSR
  // bills with no way to reach Deroi's. Same switcher the aging tab and the day book already use;
  // `can_switch` comes from the server (mig 259 — the person, not the role), so it renders
  // disabled for anyone not entitled rather than absent.
  const [companies, setCompanies] = useState([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [company, setCompany] = useState('');
  // A failed read is shown as a failure, never as "No purchase invoices yet" (3 Oct 2026).
  const [companyErr, setCompanyErr] = useState('');
  const [loadErr, setLoadErr] = useState('');
  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);
  // Report upward so the page's KPI cards follow this tab's picker. Left on its own picker
  // rather than converted to useFinanceBook: it is deployed and working, and refactoring a
  // live screen to save nine lines is not a trade worth making in the same pass as four others.
  useEffect(() => { if (company && onBook) onBook(company); }, [company, onBook]);
  useEffect(() => {
    api.get('/finance/payables-companies')
      .then(r => { setCompanies(r.data.data || []); setCanSwitch(r.data.can_switch);
                   setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || ''); })
      .catch(e => setCompanyErr(whyNotLoaded(e)));
  }, []);
  const load = useCallback(async () => {
    if (!company) return;               // never ask before we know whose book to ask for
    const qs = new URLSearchParams();
    // payee_label rides in `filters` only so the active-filter message can name the party.
    // It is not a query the server understands, so it must not be sent.
    Object.entries(filters).forEach(([k, v]) => { if (v && k !== 'payee_label') qs.set(k, v); });
    api.get('/finance/ceiling-exceptions', H()).then(x => setOverCeil(x.data.data || [])).catch(() => setOverCeil([]));
    let r;
    try { r = await api.get('/finance/purchase-invoices?' + qs.toString(), H()); }
    catch (e) { setRows([]); setSummary(null); setMonths([]); setParties([]); setLoadErr(whyNotLoaded(e)); return; }
    setLoadErr('');
    setRows(r.data.data || []); setSummary(r.data.summary);
    setMonths(r.data.months || []); setParties(r.data.parties || []);
  }, [filters, company, H]);
  useEffect(() => { load(); }, [load]);
  // BOTH FOLLOW THE BOOK PICKER, and both scope on activeCompany server-side — so without the
  // header they answered for the viewer's HOME company, which is RSR for every Deroi operator.
  // The payables list above already used H(); these two were left on `[]` and never re-ran.
  //
  // WHAT THAT COST, MEASURED: /grn-register as RSR returns 476 receipts and NOT ONE of them is
  // yarn; as Deroi it returns 12, all of them yarn. Since mig 289 the yarn receipts are Deroi's,
  // so the "⚡ From GRN" picker — which is already on this tab and already ungated — offered an
  // operator 476 receipts that could not be theirs and none of the twelve that were.
  //
  // /procurement/pos feeds the PO dropdown on "+ New Purchase Invoice". Fixing only the picker
  // would leave the manual path offering RSR's purchase orders beside a picker reading Deroi's
  // receipts — one screen contradicting itself.
  useEffect(() => {
    if (!company) return;               // never ask before we know whose book to ask for
    api.get('/procurement/pos', H()).then(r => setPos(r.data.data || [])).catch(() => {});
    api.get('/procurement/grn-register', H()).then(r => setGrns(r.data.data || [])).catch(() => {});
  }, [company, H]);

  // VOID (mig 186). The only way to undo a certified bill — /reject covers pending only.
  // Voiding cascades to the debit note in the same transaction, so the confirm says so.
  const voidInvoice = async (i) => {
    const reason = askReason(
      `Void invoice ${i.invoice_no} — ${inr(i.total_amount)} to ${i.supplier || '—'}?\n\n`
      + `It stays on the record, marked, but leaves Total Payable, aging, the forecast and every roll-up.\n`
      + `Any debit note raised on it is voided with it.\n\nReason (required):`);
    if (reason == null) return;
    try {
      const r = await api.post(`/finance/purchase-invoices/${i.id}/void`, { reason: reason.trim() });
      const d = r.data.data;
      window.alert(`Invoice ${d.invoice_no} voided.`
        + (d.debit_note_voided ? `\nDebit note ${d.debit_note_voided} voided — ${inr(d.debit_note_value_withdrawn)} withdrawn.` : '\nNo debit note was attached.')
        + (d.vendor_must_be_told ? '\n\n⚠ That debit note WAS sent to the vendor. They must be told it is withdrawn.' : ''));
      load();
    } catch (e) { window.alert(e.response?.data?.error || 'Void failed'); }
  };

  // PAYMENT HOLD (bill_holds, mig 377; raised from here since 8 Oct 2026). Stops every payment door: release,
  // cheque allocation and the recorder. A named person raises it; a shared login is refused by the server, which
  // says so. Resolving it is on the Held bills panel, as before.
  const HOLD_KINDS = { amount: 'check_amount', quantity: 'check_quantity', duplicate: 'check_duplicate', paid: 'check_payment', po: 'check_po', stop: 'do_not_pay' };
  const holdPayment = async (i) => {
    const k = askReason(`Hold payment on ${i.invoice_no} — ${inr(i.total_amount)} to ${i.supplier || '—'}?\n\n`
      + `No release, cheque allocation or payment can be made on it until the hold is resolved.\n\n`
      + `What must be checked? Type one of: amount, quantity, duplicate, paid, po, stop`);
    if (k == null) return;
    const kind = HOLD_KINDS[String(k).trim().toLowerCase()];
    if (!kind) { window.alert('Type one of: amount, quantity, duplicate, paid, po, stop'); return; }
    const detail = askReason(`Why is ${i.invoice_no} held? (required, at least twenty characters — it is the record of why nobody may pay it)`);
    if (detail == null) return;
    try {
      await api.post('/finance/bill-holds', { invoice_id: i.id, kind, detail: detail.trim() });
      window.alert(`${i.invoice_no} is held. It shows on the Held bills panel until a named person resolves it.`);
      load();
    } catch (e) { window.alert(e.response?.data?.error || 'Hold failed'); }
  };

  // ─── PICK A RECEIPT, THEN ACCEPT THE CONSEQUENCE — two decisions, two steps ─────────────
  // This was a window.prompt that PRINTED twelve GRN numbers into the dialog body and asked
  // the user to type one back. Three things were wrong with it and only one was the dialog.
  //
  // The exclusion filter matched nothing. It tested `invoice_no === 'PI-'+grn_no`, and NOT ONE
  // of the 239 purchase invoices is named that way — the real link is purchase_invoices.grn_id.
  // So every GRN read as available, including the one that already had an invoice.
  //
  // It showed 12 of 279, with nothing to say the other 267 existed.
  //
  // And the lookup ignored the filter anyway: it searched the full `grns` array, so a GRN the
  // filter meant to exclude was accepted if typed.
  //
  // A TABLE, NOT A DROPDOWN. You cannot tell which receipt you want from a number — vendor,
  // date, quantity and PO are what identify it, and a dropdown can only carry one label.
  //
  // AND THE CONFIRM STAYS SEPARATE. Choosing a receipt and accepting an irreversible
  // consequence are two decisions. One GRN gets one invoice, enforced server-side by
  // `WHERE grn_id=$1` — which does NOT exclude voided rows, so voiding the invoice does not
  // free the receipt. Picking the wrong one cannot be undone.
  const [grnPicker, setGrnPicker] = useState(false);
  const [grnSearch, setGrnSearch] = useState('');
  const [grnPick, setGrnPick] = useState(null);
  const [grnBusy, setGrnBusy] = useState(false);
  // Unused while From GRN is disabled (3 Oct); kept for its rebuild, which needs the vendor's own figures.
  // eslint-disable-next-line no-unused-vars
  const openGrnPicker = () => { setGrnPick(null); setGrnSearch(''); setGrnPicker(true); };
  const grnCandidates = grns
    .filter(g => !rows.some(r => r.grn_id && String(r.grn_id) === String(g.id)))
    // ...nor one billed through its LINES (the store path), which the line above cannot see:
    // 601 receipts were offered here and would have been billed twice. The server refuses it too.
    .filter(g => !g.invoice_nos)
    .filter(g => { const q = grnSearch.trim().toLowerCase(); if (!q) return true;
      return [g.grn_no, g.supplier_name, g.po_no, g.gpo_no].some(v => String(v || '').toLowerCase().includes(q)); });
  const createFromGrn = async () => {
    if (!grnPick) return;
    setGrnBusy(true);
    try {
      await api.post(`/finance/purchase-invoices/from-grn/${grnPick.id}`, {});
      setGrnPicker(false); setGrnPick(null); load();
    } catch (e) { window.alert(e.response?.data?.error || 'Failed to create the invoice'); }
    finally { setGrnBusy(false); }
  };

  return (
    <div>
      {grnPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 70, padding: '40px 16px', overflowY: 'auto' }}>
          <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 900, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h3 style={{ margin: 0, fontSize: 16, color: '#3B3A8F' }}>Create a purchase invoice from a goods receipt</h3>
              <button onClick={() => setGrnPicker(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>&times;</button>
            </div>
            {!grnPick ? (<>
              <div style={{ fontSize: 12, color: '#888', margin: '4px 0 10px' }}>
                {grnCandidates.length} receipt{grnCandidates.length === 1 ? '' : 's'} without an invoice. Pick one &mdash; the next step confirms it.
              </div>
              <input autoFocus style={{ ...inputStyle, width: '100%', marginBottom: 10 }} placeholder="Search vendor, GRN no, PO or GPO..."
                value={grnSearch} onChange={e => setGrnSearch(e.target.value)} />
              <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead><tr style={{ background: '#F7F6F3' }}>
                    {['Vendor', 'Received', 'Quantity', 'PO', 'GRN', ''].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 11, borderBottom: '1px solid #eee' }}>{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {grnCandidates.length === 0
                      ? <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#aaa' }}>Nothing matches.</td></tr>
                      : grnCandidates.map(g => (
                        <tr key={g.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '7px 10px', fontWeight: 600 }}>{g.supplier_name || '-'}</td>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{g.received_date ? fmtD(g.received_date) : '-'}</td>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{num(g.received_qty)} {g.unit || ''}</td>
                          <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 11.5 }}>{g.po_no || '-'}{g.gpo_no ? <span style={{ color: '#888' }}> &middot; GPO {g.gpo_no}</span> : null}</td>
                          <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 11.5, color: '#888' }}>{g.grn_no}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                            <button onClick={() => setGrnPick(g)} style={{ padding: '4px 12px', background: '#EEEDF8', border: 'none', borderRadius: 4, color: '#3B3A8F', fontSize: 11.5, cursor: 'pointer', fontWeight: 600 }}>Select</button>
                          </td>
                        </tr>))}
                  </tbody>
                </table>
              </div>
            </>) : (<>
              <div style={{ margin: '12px 0', background: '#F7F6F3', borderRadius: 6, padding: '12px 14px', fontSize: 13 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{grnPick.supplier_name || '-'}</div>
                <div>{num(grnPick.received_qty)} {grnPick.unit || ''} received {grnPick.received_date ? fmtD(grnPick.received_date) : '-'}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#666', marginTop: 4 }}>{grnPick.grn_no} &middot; {grnPick.po_no || '-'}{grnPick.gpo_no ? ' &middot; GPO ' + grnPick.gpo_no : ''}</div>
              </div>
              <div style={{ background: '#FCEBEB', border: '1px solid #F0C0C0', borderRadius: 6, padding: '10px 12px', fontSize: 12.5, color: '#A32D2D' }}>
                <strong>This cannot be undone.</strong> A goods receipt can carry only one purchase invoice, and voiding
                that invoice does not release the receipt &mdash; it stays consumed. If this is the wrong receipt, go back now.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
                <button onClick={() => setGrnPick(null)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>&larr; Back to the list</button>
                <button disabled={grnBusy} onClick={createFromGrn} style={{ padding: '8px 20px', background: '#0d9488', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{grnBusy ? 'Creating...' : 'Create the invoice'}</button>
              </div>
            </>)}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto', background: '#F7F6F3', padding: '6px 8px', borderRadius: 8 }}>
        {/* WHOSE PAYABLES. Rendered disabled rather than hidden when the caller cannot switch,
            so the column always says which book is on screen — a list that does not name its
            company is how RSR's 395 bills read as Deroi's. */}
        <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)}
          title={canSwitch ? 'Whose payables are shown' : 'You can only see your own company'}
          style={{ padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontWeight: 700,
            color: '#3B3A8F', background: canSwitch ? '#EEEDF8' : '#f5f5f5', flex: '0 0 auto' }}>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {/* Invoice No and PO No are SEPARATE boxes on purpose. A single combined box was
            measured against the live data and fails here: PO numbers like TPO-4252601xx
            contain the run "252", so searching an invoice number of "252" would return 29
            rows instead of 1. 22 of the 85 short invoice numbers collide that way. Both
            are server-side so the KPI strip and roll-ups stay in step with the table. */}
        <input style={{ ...inputStyle, width: 120 }} placeholder="Invoice no…" value={filters.invoice_no || ''} onChange={e => setFilters({ ...filters, invoice_no: e.target.value })} />
        <input style={{ ...inputStyle, width: 130 }} placeholder="PO no…" value={filters.po_no || ''} onChange={e => setFilters({ ...filters, po_no: e.target.value })} />
        <input style={{ ...inputStyle, width: 130 }} placeholder="Supplier…" value={filters.supplier || ''} onChange={e => setFilters({ ...filters, supplier: e.target.value })} />
        <select style={{ ...inputStyle, width: 110 }} value={filters.po_type || ''} onChange={e => setFilters({ ...filters, po_type: e.target.value })}>
          <option value="">All PO types</option>{['fabric', 'yarn', 'trim', 'trims', 'other'].map(t => <option key={t}>{t}</option>)}</select>
        <select style={{ ...inputStyle, width: 110 }} value={filters.status || ''} onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>{['unpaid', 'partial', 'paid'].map(s => <option key={s}>{s}</option>)}</select>
        <input type="date" style={{ ...inputStyle, width: 125 }} value={filters.from || ''} onChange={e => setFilters({ ...filters, from: e.target.value })} />
        <input type="date" style={{ ...inputStyle, width: 125 }} value={filters.to || ''} onChange={e => setFilters({ ...filters, to: e.target.value })} />
        {Object.values(filters).some(Boolean) && <button onClick={() => setFilters({})} style={{ ...inputStyle, cursor: 'pointer', background: 'white' }}>✕</button>}
        {/* DISABLED (Rajan, 3 Oct 2026). A from-GRN bill took its number, date and amount from OUR receipt,
            so nothing could compare it with a vendor's claim — the self-derived pattern ruled out for
            invoice 524. Zero bills were ever made this way. It comes back (size S) requiring the vendor's
            invoice number, date and claimed amount, with the line at the PO rate. */}
        <button disabled title="Disabled until it records the vendor's own invoice number, date and amount — a bill built only from our receipt has nothing to check a vendor's claim against. Use + New Purchase Invoice."
          style={{ ...btnPrimary, marginLeft: 'auto', background: '#ccc', cursor: 'not-allowed', flex: '0 0 auto' }}>⚡ From GRN (disabled)</button>
        <span style={{ fontSize: 11, color: '#888', flex: '0 0 auto', maxWidth: 260 }}>From GRN is off until it records the vendor's invoice number, date and amount. Use + New Purchase Invoice.</span>
        <button onClick={() => setShowNew(true)} style={{ ...btnPrimary, flex: '0 0 auto' }}>+ New Purchase Invoice</button>
      </div>
      {/* WHOSE JOB THE NEXT STEP IS. Recording a bill and clearing it are different hands: the
          certify and pay controls are hidden for anyone who may not use them, which is right,
          but hiding them left a Deroi operator looking at a list with no controls and no
          statement of why. One line is cheaper than a support call. */}
      <div style={{ margin: '0 0 12px', padding: '7px 11px', background: '#F7F6F3', border: '1px solid #E8E7E7',
                    borderRadius: 6, fontSize: 11.5, color: '#666' }}>
        A recorded bill is <strong>certified by Finance</strong> and <strong>paid by Accounts</strong>. Until it is certified
        it shows <em>awaiting Finance</em> and is excluded from Total Payable.
      </div>
      {summary && (
        <div style={{ display: 'flex', gap: 14, marginBottom: 12, fontSize: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Invoiced: <strong>{inr(summary.invoiced)}</strong></span>
          <span style={{ color: '#166534' }}>Paid: <strong>{inr(summary.paid)}</strong></span>
          <span style={{ color: '#C2410C' }}>Payable: <strong>{inr(summary.payable)}</strong></span>
          {/* Uncertified is NOT inside Payable — it is the liability the payable is not yet
              carrying. Kept on the headline strip, never buried in the breakdown. */}
          {summary.voided_count > 0 && (
            <span style={{ color: '#A32D2D' }} title="Voided bills stay listed but count towards no figure on this strip.">
              Voided: <strong>{summary.voided_count}</strong> (excluded from every total)
            </span>
          )}
          {summary.uncertified_count > 0 && (
            <span style={{ color: '#A32D2D', background: '#FCEBEB', padding: '3px 10px', borderRadius: 20 }}
              title="Bills awaiting certification are excluded from Payable — until they are certified, Total Payable understates the liability.">
              Awaiting certification: <strong>{inr(summary.uncertified)}</strong> across {summary.uncertified_count} bill{summary.uncertified_count === 1 ? '' : 's'}
            </span>
          )}
          {/* LEAD WITH THE NUMBER THAT WOULD BE WRONG (Rajan, 3 Oct): money paid on a bill nothing
              approved. The bills paid on a payment-request approval are properly approved under the
              ruling of 3 Oct, so they are the detail behind the headline, never the alarm. */}
          {summary.paid_unapproved_count != null && (
            <span style={{ color: summary.paid_unapproved_count ? '#A32D2D' : '#166534',
                           background: summary.paid_unapproved_count ? '#FCEBEB' : '#EAF7EE', padding: '3px 10px', borderRadius: 20 }}
              title={`Paid on a bill with no approval of any kind — neither certified nor approved through a payment request.${summary.paid_uncertified_by_request ? ` Behind this: ${summary.paid_uncertified_by_request} bill${summary.paid_uncertified_by_request === 1 ? '' : 's'} paid on a payment-request approval (ad-hoc payables, no goods to certify), which is their approving act.` : ''}`}>
              Paid with no approval: <strong>{summary.paid_unapproved_count ? `${inr(summary.paid_unapproved_amount)} across ${summary.paid_unapproved_count} bill${summary.paid_unapproved_count === 1 ? '' : 's'}` : 'none'}</strong>
              {summary.paid_uncertified_by_request > 0 && (
                <span style={{ fontWeight: 400, color: '#555' }}> · {summary.paid_uncertified_by_request} paid on a payment-request approval ({inr(summary.paid_uncertified - (summary.paid_unapproved_amount || 0))})</span>)}
            </span>
          )}
        </div>
      )}
      <RollUp title="📅 Month-wise expenditure" open={openMonths} onToggle={() => setOpenMonths(o => !o)}
        hint={`· ${months.length} month${months.length === 1 ? '' : 's'} · by invoice date · totals + uncertified`}>
        <MonthTable months={months} />
      </RollUp>
      <RollUp title="🏭 Party-wise totals" open={openParties} onToggle={() => setOpenParties(o => !o)}
        hint={`· ${parties.length} suppliers · largest outstanding first · click a row to filter`}>
        {/* Phase 1c — a party row filters on payee_id, not the display text. Text can no
            longer reach a party's whole ledger: "FINE THREADS" ILIKE-matches 7 of its 20
            invoices and misses the 13 booked as "fine thread". Unassigned filters as 'none'. */}
        <PartyTable parties={parties} onPick={(p) => {
          setFilters({ ...filters, supplier: '', payee_id: p.payee_id || 'none', payee_label: p.supplier });
          setOpenParties(false);
        }} />
      </RollUp>
      <BillHoldsBanner onChanged={load} />
      <DocumentDatesPanel onChanged={load} />
      {selBills.length >= 2 && (() => {
        const picked = rows.filter(x => selBills.includes(x.id));
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#F6FAFF', border: '1px solid #C4DAF2', borderRadius: 8, marginBottom: 10, fontSize: 12 }}>
            <span><strong>{picked.length}</strong> bills · {picked[0]?.supplier} · <strong>{inr(picked.reduce((t, x) => t + num(x.balance), 0))}</strong> — one request per bill, each approved on its own</span>
            <span style={{ flex: 1 }} />
            <button onClick={() => setSelBills([])} style={{ ...btnPrimary, padding: '5px 10px', fontSize: 11, background: 'transparent', color: '#444', border: '1px solid #ccc' }}>Clear</button>
            <button onClick={() => setReleaseFor(picked)} style={{ ...btnPrimary, padding: '5px 12px', fontSize: 11, background: '#1D4E89' }}>Raise {picked.length} releases</button>
          </div>);
      })()}
      <RollUp title={`🧾 Raised over the 2× receipt ceiling (${overCeil.length})`} open={openOver} onToggle={() => setOpenOver(o => !o)}
        hint="Every bill a management exception let through the 2× ceiling — who declared it, why, and who raised, approved and paid it.">
        {overCeil.length === 0 ? <div style={{ fontSize: 12, color: '#888', padding: 8 }}>No exceptions declared.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>{['Bill', 'Supplier', 'Asks', 'Receipts', '×', 'Basis', 'Reason', 'Declared by', 'Request', 'Approved', 'Paid by'].map(h => <th key={h} style={thS}>{h}</th>)}</tr></thead>
            <tbody>{overCeil.map(x => (
              <tr key={x.exception_id} style={{ opacity: x.withdrawn_at ? 0.5 : 1 }}>
                <td style={tdS}>{x.invoice_no}</td><td style={tdS}>{x.supplier}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{inr(x.asks)}</td><td style={{ ...tdS, textAlign: 'right' }}>{inr(x.ceiling)}</td>
                <td style={tdS}>{x.times_receipt}</td><td style={tdS}>{(x.basis || '').replace(/_/g, ' ')}</td>
                <td style={{ ...tdS, maxWidth: 260 }}>{x.reason}{x.withdrawn_at ? ` — WITHDRAWN by ${x.withdrawn_by}: ${x.withdraw_reason}` : ''}</td>
                <td style={tdS}>{x.declared_by}<br /><span style={{ color: '#888' }}>{fmtD(x.declared_at)}</span></td>
                <td style={tdS}>{x.request_no ? `${x.request_no} · ${x.request_status}` : '—'}</td>
                <td style={tdS}>{x.approved_by || '—'}</td><td style={tdS}>{x.paid_by || '—'}</td>
              </tr>))}</tbody>
          </table>)}
      </RollUp>
      {releaseFor && <RaiseReleaseModal bills={releaseFor} isMgmt={isMgmt}
        onClose={() => setReleaseFor(null)} onDone={() => { setReleaseFor(null); setSelBills([]); load(); }} />}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['', 'Invoice No', 'Supplier', 'GPO', 'PO No', 'Type', 'Amount', 'GST', 'Total', 'Due', 'Status', 'Balance', ''].map((h, k) => <th key={k} style={thS}>{h}</th>)}
        </tr></thead>
        <tbody>
          {/* An empty table under an active filter must never read as "there are none" —
              it has to say which filter emptied it, or the search looks broken. */}
          {(companyErr || loadErr) ? <tr><td colSpan={13} style={{ padding: 22, textAlign: 'center', color: '#A32D2D' }}>
            {companyErr ? `Could not load the list of books (${companyErr}), so no purchase invoices were asked for. This is not an empty list.`
                        : `Could not load the purchase invoices (${loadErr}). This is not an empty list.`}
          </td></tr>
          : !company ? <tr><td colSpan={13} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>Loading…</td></tr>
          : rows.length === 0 ? <tr><td colSpan={13} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>
            {Object.values(filters).some(Boolean)
              ? `Nothing matched ${[filters.invoice_no && `invoice “${filters.invoice_no}”`,
                                    filters.po_no && `PO “${filters.po_no}”`,
                                    filters.supplier && `supplier “${filters.supplier}”`,
                                    filters.payee_id && `party “${filters.payee_label || filters.payee_id}”`,
                                    filters.po_type && `type ${filters.po_type}`,
                                    filters.status && `status ${filters.status}`,
                                    (filters.from || filters.to) && 'that date range'].filter(Boolean).join(' + ')}.`
              : 'No purchase invoices yet.'}
          </td></tr>
            : rows.map(i => (
              <tr key={i.id} style={{ borderBottom: '1px solid #f5f5f5', background: selBills.includes(i.id) ? '#EEF4FC' : undefined }}>
                <td style={tdS}>{raisable(i) && (() => {
                  const first = rows.find(x => x.id === selBills[0]);
                  const ok = !first || supKey(first) === supKey(i);
                  return <input type="checkbox" checked={selBills.includes(i.id)} disabled={!ok && !selBills.includes(i.id)}
                    title={ok ? 'Raise several bills of this supplier together — still one request per bill' : 'Several at once is for ONE supplier — clear the ticks to start another'}
                    onChange={() => setSelBills(s => s.includes(i.id) ? s.filter(x => x !== i.id) : [...s, i.id])} />;
                })()}</td>
                <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>
                  {i.hold_kind && <div title={i.hold_title} style={{ fontFamily: 'inherit', marginBottom: 3, display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: 9.5, fontWeight: 800,
                    background: i.hold_kind === 'do_not_pay' ? '#A32D2D' : '#B45309', color: '#fff' }}>{HOLD_LABEL[i.hold_kind] || '⚠ HELD'}</div>}
                  <div>{i.invoice_no}{i.file_url && <a href={fileHref(i.file_url)} target="_blank" rel="noreferrer" style={{ marginLeft: 4 }}>📎</a>}</div>{i.invoice_date_corrected_from && <div style={{ fontSize: 10, color: '#7A1F1F' }} title="Recorded date correction (mig 387)">📅 date corrected from {fmtD(i.invoice_date_corrected_from)}</div>}</td>
                <td style={tdS}>{i.supplier || '—'}</td>
                <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{i.gpo_no || '—'}</td>
                <td style={{ ...tdS, fontSize: 11 }}><PoList po_nos={i.po_nos} po_no={i.po_no} /></td>
                <td style={tdS}>{i.po_type || '—'}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{inr(i.amount)}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>{inr(i.gst_amount)}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{inr(i.total_amount)}</td>
                <td style={{ ...tdS, color: i.due_date && new Date(i.due_date) < new Date() && i.payment_status !== 'paid' ? '#A32D2D' : '#333', fontWeight: 600 }}>{fmtD(i.due_date)}</td>
                <td style={tdS}>{payTag(i.payment_status)}
                  {/* REVIEW state, beside PAYMENT state. The row showed only payment_status, so a
                      bill awaiting certification looked identical to one Finance had already
                      passed — and a bill recorded by a Deroi operator now lands 'pending' by
                      design. A queue that cannot say where a bill has got to is not a queue. */}
                  {i.review_status && i.review_status !== 'approved' && (
                    <span style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                                   background: i.review_status === 'rejected' ? '#FCEBEB' : '#FFF8E6',
                                   color: i.review_status === 'rejected' ? '#A32D2D' : '#7A5A00' }}
                      title={i.review_status === 'pending'
                        ? 'Recorded, not yet certified by Finance — excluded from Total Payable until it is.'
                        : 'Rejected at certification.'}>
                      {i.review_status === 'pending' ? 'awaiting Finance' : 'rejected'}</span>
                  )}
                  {/* WHAT APPROVED IT, from the bill (mig 358). A request-approved payable names its
                      request and every approving decision; a certified bill names its certifier. */}
                  {i.review_status === 'approved' && i.approval_basis === 'payment_request' && (
                    <div style={{ fontSize: 10, color: '#555', marginTop: 3, maxWidth: 260 }}
                      title="An ad-hoc payable has no goods and no lines to certify; its approving act is its payment request (ruling of 3 Oct 2026).">
                      Approved via request <strong>{i.approval_request_no}</strong>: {i.approval_trail || i.final_approver || '—'}
                      {i.approval_raised_by ? ` · raised by ${i.approval_raised_by}` : ''}</div>
                  )}
                  {i.review_status === 'approved' && i.approval_basis === 'certification' && i.certified_by_name && (
                    <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>Certified by {i.certified_by_name} · {fmtD(i.certified_at)}</div>
                  )}
                  {i.review_status === 'approved' && i.approval_basis === 'column_default' && (
                    <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 3, maxWidth: 260 }}>
                      Approved by nobody: this bill took a column default on 16 Jul 2026, before certification existed. Recorded as it is.</div>
                  )}
                </td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: num(i.balance) > 0 ? '#C2410C' : '#166534' }}>{inr(i.balance)}</td>
                {/* A voided invoice stays in the list, marked, never hidden — the same
                    treatment a cancelled PO gets. It counts towards nothing. */}
                <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                  {i.voided_at ? (
                    <span title={`Voided by ${i.voided_by_name || '—'} on ${fmtD(i.voided_at)} — ${i.void_reason || ''}`}
                      style={{ fontSize: 10, fontWeight: 700, color: '#A32D2D', background: '#FCEBEB', borderRadius: 10, padding: '2px 9px' }}>
                      ⃠ VOID
                    </span>
                  ) : (
                    <>
                      {/* DOOR B CLOSED (3 Oct 2026): a vendor bill is raised as a release, not paid here. */}
                      {raisable(i) && <button onClick={() => setReleaseFor([i])} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: '#1D4E89' }}>Raise release</button>}
                      {Number(i.amount_paid) > 0 && <button onClick={() => setPayInv(i)} title="This bill's payment ledger" style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: 'transparent', color: '#166534', border: '1px solid #b7dcc4', marginLeft: 6 }}>Payments</button>}
                      {i.review_status === 'approved' && i.payment_status !== 'paid' && ['accounts', 'admin', 'management', 'finance_approver'].includes(user?.role)
                        && <button onClick={() => holdPayment(i)} title="Stop every payment on this bill until a named person resolves the hold"
                          style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: 'transparent', color: '#92400E', border: '1px solid #F5D9A8', marginLeft: 6 }}>Hold payment</button>}
                      {isMgmt && <button onClick={() => voidInvoice(i)} title="Void this bill — it stays on the record but counts towards nothing"
                        style={{ ...btnPrimary, padding: '4px 10px', fontSize: 10, background: 'transparent', color: '#A32D2D', border: '1px solid #f0cccc', marginLeft: 6 }}>Void</button>}
                    </>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {showNew && <NewPurchaseInvModal pos={pos} companies={companies} company={company} canSwitch={canSwitch}
        onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {payInv && <InvoicePaymentModal inv={payInv} kind="purchase" canReverse={canDisburse}
        onClose={() => setPayInv(null)} onChanged={load} />}
    </div>
  );
}

// ── BILL HOLDS BANNER (mig 377) — items put in front of Accounts on the screens they already open ──────
// Email is partly blocked and WhatsApp has never worked; a hold on a bill is the notice. Each open hold
// is listed with what to do, and is resolved here by a named person saying what the paper showed.
// HELD BILLS (display, 4 Oct 2026, Rajan): Accounts could not identify the party from "bill no · amount".
// Every chip now reads PARTY · bill no · amount · bill date, the party taken from the LINKED payee (followed
// through a merge to its supplier or Deroi vendor) — never the typed supplier text. A bill with no linked party
// says so in words. Within each reason block the chips are grouped by party, so one person holding one party's
// file works its bills in one go. A chip OPENS ITS BILL, and the hold is resolved there, looking at the bill.
// Which bills are held, and why, is unchanged: this is display only.
const NO_PARTY = 'no party linked';
const fmtBillDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'no date');
const partyOf = (h) => h.party_name || NO_PARTY;
// A block heading states ONLY what is true of every bill in it (Rajan, 4 Oct 2026). Each hold's detail is split
// into sentences; the heading keeps the sentences that appear, word for word, in EVERY bill's detail in the block.
// A sentence about one bill ("This bill is dated 29 Jun 2026…") belongs to that bill, and is shown when it is opened.
const sentencesOf = (t) => String(t || '').split(/(?<=[.?!])\s+(?=["“(A-Z0-9])/).map(x => x.trim()).filter(Boolean);
export const sharedDetail = (details) => {
  const sets = details.map(d => new Set(sentencesOf(d)));
  return sentencesOf(details[0]).filter(x => sets.every(st => st.has(x))).join(' ');
};

function HeldBillPanel({ holdId, onClose, onResolved }) {
  const [bill, setBill] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [note, setNote] = useState({});
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/finance/bill-holds/${holdId}/bill`).then(r => setBill(r.data.data))
    .catch(e => setLoadErr(e.response?.data?.error || 'Could not open this bill')), [holdId]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (h) => {
    setErr(''); setBusy(true);
    try { await api.post(`/finance/bill-holds/${h.id}/resolve`, { resolution: (note[h.id] || '').trim() }); setNote({ ...note, [h.id]: '' }); await load(); onResolved && onResolved(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  const td = { padding: '5px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 12 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2100, overflowY: 'auto', padding: '24px 0' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, width: '94%', maxWidth: 760, margin: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        {loadErr && <div style={{ color: '#A32D2D' }}>{loadErr}</div>}
        {!bill && !loadErr && <div style={{ color: '#888' }}>Opening the bill…</div>}
        {bill && <>
          <div style={{ fontSize: 15, fontWeight: 800, color: bill.party_name ? '#1D4E89' : '#A32D2D' }}>{bill.party_name || 'No party linked to this bill'}</div>
          {!bill.party_name && <div style={{ fontSize: 11, color: '#A32D2D' }}>This bill has no linked payee, so the system cannot say whose it is. Link the payee on the bill first.</div>}
          <div style={{ fontSize: 13, marginTop: 4 }}><b style={{ fontFamily: 'monospace' }}>{bill.invoice_no}</b> · dated <b>{fmtBillDate(bill.invoice_date)}</b>{bill.po_no ? <> · PO {bill.po_no}</> : null}{bill.gpo_no ? <> · GPO {bill.gpo_no}</> : null}</div>
          <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>
            Amount ₹{Number(bill.amount || 0).toLocaleString('en-IN')} + GST {bill.gst_pct || 0}% ₹{Number(bill.gst_amount || 0).toLocaleString('en-IN')} = <b>₹{Number(bill.total_amount || 0).toLocaleString('en-IN')}</b>
            {' '}· {bill.review_status} · {bill.payment_status}{bill.due_date ? <> · due {fmtBillDate(bill.due_date)}</> : null}</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>{bill.file_url
            ? <a href={fileHref(bill.file_url)} target="_blank" rel="noreferrer">📎 Open the bill document</a>
            : <span style={{ color: '#B45309' }}>No bill document attached to this record.</span>}</div>
          {bill.lines?.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
              <thead><tr>{['Item', 'Qty', 'Rate', 'PO'].map(x => <th key={x} style={{ ...td, textAlign: 'left', color: '#888', fontWeight: 600 }}>{x}</th>)}</tr></thead>
              <tbody>{bill.lines.map((l, i) => (
                <tr key={i}><td style={td}>{l.item_desc}{l.size ? ` (${l.size})` : ''}</td><td style={td}>{Number(l.invoiced_qty || 0).toLocaleString('en-IN')}</td>
                  <td style={td}>₹{Number(l.rate || 0).toLocaleString('en-IN')}</td><td style={{ ...td, fontFamily: 'monospace' }}>{l.line_po || '—'}</td></tr>))}</tbody>
            </table>)}
          <div style={{ marginTop: 12 }}>
            {bill.holds.map(h => (
              <div key={h.id} style={{ border: '1px solid ' + (h.resolved_at ? '#d1e7d1' : '#f3d6d6'), background: h.resolved_at ? '#f6fbf6' : '#fff8f8', borderRadius: 8, padding: '8px 10px', marginBottom: 8, fontSize: 12 }}>
                <div style={{ fontWeight: 800, color: h.resolved_at ? '#166534' : (h.kind === 'do_not_pay' ? '#A32D2D' : '#B45309') }}>
                  {h.resolved_at ? '✓ ' : (h.kind === 'do_not_pay' ? '⛔ ' : '⚠ ')}{h.title}</div>
                <div style={{ color: '#444', margin: '3px 0' }}>{h.detail}</div>
                <div style={{ color: '#888', fontSize: 11 }}>raised by {h.raised_by_name}{h.resolved_at ? <> · resolved by {h.resolved_by_name}: “{h.resolution}”</> : null}</div>
                {!h.resolved_at && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <textarea value={note[h.id] || ''} onChange={e => setNote({ ...note, [h.id]: e.target.value })}
                      placeholder="What the paper showed, and what was done (at least twenty characters). This lifts this hold on this one bill."
                      style={{ flex: 1, minHeight: 44, padding: 7, border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }} />
                    <button disabled={busy || (note[h.id] || '').trim().length < 20} onClick={() => resolve(h)}
                      style={{ padding: '7px 12px', background: (note[h.id] || '').trim().length < 20 ? '#bbb' : '#166534', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Resolve</button>
                  </div>)}
              </div>))}
          </div>
          {err && <div style={{ color: '#A32D2D', fontSize: 11, marginTop: 4 }}>{err}</div>}
        </>}
        <div style={{ textAlign: 'right', marginTop: 10 }}><button onClick={onClose} style={{ padding: '7px 14px', background: '#f0f2f8', color: '#444', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Close</button></div>
      </div>
    </div>
  );
}

export function BillHoldsBanner({ onChanged }) {
  const [holds, setHolds] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get('/finance/bill-holds?open=1').then(r => setHolds(r.data.data || [])).catch(() => setHolds([])), []);
  useEffect(() => { load(); }, [load]);
  if (!holds || holds.length === 0) return null;
  // Reason blocks exactly as before (by title, in the server's order); inside each, grouped by party.
  const groups = holds.reduce((m, h) => { (m[h.title] = m[h.title] || []).push(h); return m; }, {});
  const byParty = (hs) => {
    const m = new Map();
    [...hs].sort((a, b) => (a.party_name ? 0 : 1) - (b.party_name ? 0 : 1) || partyOf(a).localeCompare(partyOf(b))
      || String(a.invoice_date || '').localeCompare(String(b.invoice_date || '')) || String(a.invoice_no).localeCompare(String(b.invoice_no)))
      .forEach(h => { const k = h.party_key || NO_PARTY; if (!m.has(k)) m.set(k, []); m.get(k).push(h); });
    return [...m.values()];
  };
  return (
    <div style={{ marginBottom: 12, border: '2px solid #A32D2D', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '9px 13px', background: '#FCEBEB', color: '#A32D2D', fontSize: 13, fontWeight: 800 }}>
        {holds.length} bill{holds.length === 1 ? '' : 's'} held for Accounts — they cannot be released or paid until resolved. Click a bill to open it.
      </div>
      {Object.entries(groups).map(([title, hs]) => (
        <div key={title} style={{ padding: '9px 13px', borderTop: '1px solid #f3d6d6', fontSize: 12 }}>
          <div style={{ fontWeight: 800, color: hs[0].kind === 'do_not_pay' ? '#A32D2D' : '#B45309' }}>{hs[0].kind === 'do_not_pay' ? '⛔' : '⚠'} {title} · {hs.length} bill{hs.length === 1 ? '' : 's'}</div>
          {(() => { const shared = sharedDetail(hs.map(h => h.detail)); return shared
            ? <div style={{ color: '#444', margin: '4px 0 6px', lineHeight: 1.45 }}>{shared}</div>
            : <div style={{ color: '#888', margin: '4px 0 6px', fontStyle: 'italic' }}>Each bill here has its own explanation. Open a bill to read it.</div>; })()}
          {byParty(hs).map(ps => (
            <div key={ps[0].party_key || NO_PARTY} style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: ps[0].party_name ? '#1D4E89' : '#A32D2D' }}>{partyOf(ps[0])} · {ps.length} bill{ps.length === 1 ? '' : 's'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 3 }}>
                {ps.map(h => (
                  <button key={h.id} onClick={() => setOpen(h.id)} title="Open this bill"
                    style={{ padding: '3px 9px', borderRadius: 12, border: '1px solid #e2b4b4', background: '#fff', fontSize: 11, cursor: 'pointer' }}>
                    <span style={{ color: h.party_name ? '#1D4E89' : '#A32D2D', fontWeight: 700 }}>{partyOf(h)}</span>
                    {' · '}<span style={{ fontFamily: 'monospace' }}>{h.invoice_no}</span>
                    {' · ₹'}{Number(h.total_amount || 0).toLocaleString('en-IN')}
                    {' · '}{fmtBillDate(h.invoice_date)}</button>))}
              </div>
            </div>))}
        </div>
      ))}
      {open && <HeldBillPanel holdId={open} onClose={() => setOpen(null)} onResolved={() => { load(); onChanged && onChanged(); }} />}
    </div>
  );
}

// ── RAISE FROM THE BILL (3 Oct 2026) ────────────────────────────────────────────────────────────
// One click plus confirm. The preview runs the same resolution the raise does (the bill's own
// receipts, summed; the 2x ceiling; any declared exception), so this dialog cannot promise what the
// server then refuses. Several bills of ONE supplier become several requests — never one request.
const BAND_LABEL = { clean: ['within its receipts', '#166534'], warn: ['1×–2× its receipts — allowed, and said', '#B45309'],
                     over: ['more than 2× its receipts', '#A32D2D'], no_receipt: ['no receipt on its lines', '#A32D2D'] };
function RaiseReleaseModal({ bills, isMgmt, onClose, onDone }) {
  const [prev, setPrev] = useState(null);       // [{...preview}] in bill order
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [ex, setEx] = useState({ basis: 'price_rise', reason: '' });
  const loadPrev = useCallback(() => Promise.all(bills.map(b =>
      api.get('/requests/release-preview?invoice_id=' + b.id).then(r => r.data.data)
        .catch(e => ({ invoice_id: b.id, invoice_no: b.invoice_no, error: e.response?.data?.error || 'Could not load this bill' }))))
    .then(setPrev), [bills]);
  useEffect(() => { loadPrev(); }, [loadPrev]);
  const single = bills.length === 1 ? (prev || [])[0] : null;
  const blocked = (p) => p.error || (p.holds && p.holds.length) || p.held_by || p.band === 'no_receipt' || (p.band === 'over' && !p.exception);
  const raise = async () => {
    setErr(''); setBusy(true);
    try {
      if (bills.length === 1) {
        const r = await api.post('/requests', { payment_kind: 'release', invoice_id: bills[0].id, submit: true });
        setResults([{ invoice_no: bills[0].invoice_no, ok: true, request_no: r.data.data.request_no, amount: r.data.data.amount, warning: r.data.warning }]);
      } else {
        const r = await api.post('/requests/release-batch', { invoice_ids: bills.map(b => b.id) });
        setResults(r.data.data);
      }
    } catch (e) {
      if (Array.isArray(e.response?.data?.data)) setResults(e.response.data.data);
      else setErr(e.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };
  const declare = async () => {
    setErr(''); setBusy(true);
    try { await api.post(`/finance/purchase-invoices/${single.invoice_id}/ceiling-exception`, { basis: ex.basis, reason: ex.reason }); setEx({ basis: 'price_rise', reason: '', open: false }); await loadPrev(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2100, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '94%', maxWidth: 680, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#1D4E89', margin: '-22px -22px 16px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>
            {bills.length === 1 ? `Raise release — ${bills[0].invoice_no}` : `Raise ${bills.length} releases — ${bills[0].supplier}`}</h3></div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#666' }}>
          {bills.length === 1 ? 'One payment request for this bill, measured against its own receipts. It then goes for approval and is paid from Requests → To pay.'
            : 'One payment request PER BILL — each approved and paid on its own. A bill that is refused does not stop the others.'}</p>
        {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{err}</div>}
        {!prev ? <div style={{ fontSize: 12, color: '#888' }}>Checking the bill{bills.length > 1 ? 's' : ''}…</div> : results ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>{['Bill', 'Result'].map(h => <th key={h} style={thS}>{h}</th>)}</tr></thead>
            <tbody>{results.map(r => (
              <tr key={r.invoice_no}><td style={tdS}>{r.invoice_no}</td>
                <td style={{ ...tdS, color: r.ok ? '#166534' : '#A32D2D' }}>{r.ok ? `✅ ${r.request_no} · ${inr(r.amount)} — pending approval${r.warning ? ' · ⚠ ' + r.warning : ''}` : `✗ ${r.error}`}</td></tr>))}</tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>{['Bill', 'Asks', 'Its receipts', 'Band'].map(h => <th key={h} style={thS}>{h}</th>)}</tr></thead>
            <tbody>{prev.map(p => (
              <tr key={p.invoice_id}>
                <td style={tdS}>{p.invoice_no}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{p.error ? '—' : inr(p.outstanding)}</td>
                <td style={{ ...tdS }}>{p.error ? '—' : <>{inr(p.ceiling)}<div style={{ fontSize: 10, color: '#888' }}>{(p.receipts || []).map(g => g.grn_no).join(', ') || 'none on its lines'}</div></>}</td>
                <td style={{ ...tdS, color: (BAND_LABEL[p.band] || ['', '#A32D2D'])[1] }}>
                  {p.error ? p.error
                    : (p.holds && p.holds.length) ? <span style={{ color: '#A32D2D', fontWeight: 700 }} title={p.holds[0].detail}>HELD — {p.holds[0].title}</span>
                    : p.held_by ? `Already on ${p.held_by.request_no} (${p.held_by.status})`
                    : <>{BAND_LABEL[p.band]?.[0]}{p.times_receipt ? ` (${p.times_receipt}×)` : ''}
                        {p.band === 'over' && (p.exception
                          ? <div style={{ color: '#7A5A00' }}>Exception declared by {p.exception.declared_by} — {p.exception.basis.replace(/_/g, ' ')}</div>
                          : <div>Check it against the paper invoice before paying — usually the bill header, the PO rate or the GRN quantity is keyed wrong, and the fix is to correct it.</div>)}</>}
                </td>
              </tr>))}</tbody>
          </table>
        )}
        {/* THE DECLARED EXIT (mig 375) — management only, one bill, a basis and a substantive reason. */}
        {/* CORRECT FIRST, EXCEPT LAST: the exception form is collapsed behind a statement that the paper was
            checked — an override offered first is the one people reach for. */}
        {!results && single && !single.error && !single.held_by && single.band === 'over' && !single.exception && isMgmt && !ex.open && (
          <div style={{ marginTop: 10, fontSize: 11, color: '#777' }}>
            Checked the paper invoice and the bill is right?{' '}
            <button onClick={() => setEx(x => ({ ...x, open: true }))} style={{ background: 'none', border: 'none', color: '#7A5A00', textDecoration: 'underline', cursor: 'pointer', fontSize: 11, padding: 0 }}>Declare a ceiling exception</button>
          </div>
        )}
        {!results && single && !single.error && !single.held_by && single.band === 'over' && !single.exception && isMgmt && ex.open && (
          <div style={{ marginTop: 12, border: '1px solid #F3C77A', background: '#FFF8E6', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7A5A00', textTransform: 'uppercase', marginBottom: 6 }}>Declare a ceiling exception — only after the paper invoice confirms the bill</div>
            <div style={{ fontSize: 11, color: '#7A5A00', marginBottom: 8 }}>For a bill that is RIGHT at more than twice its receipts — a price rise, a part-delivered GRN. A wrong PO rate or a bill header keyed wrong is corrected, not excepted. This is recorded with your name and listed in the over-ceiling report.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={ex.basis} onChange={e => setEx(x => ({ ...x, basis: e.target.value }))} style={{ ...inputStyle, flex: '0 0 190px' }}>
                <option value="price_rise">Price rise</option><option value="part_delivered_grn">Part-delivered GRN</option>
                <option value="rate_convention">Rate convention (PO unit differs)</option><option value="other">Other — say what</option>
              </select>
              <input value={ex.reason} onChange={e => setEx(x => ({ ...x, reason: e.target.value }))} placeholder="Why this bill is right (at least twenty characters)" style={{ ...inputStyle, flex: 1 }} />
              <button disabled={busy || ex.reason.trim().length < 20} onClick={declare} style={{ ...btnPrimary, background: '#7A5A00' }}>Declare</button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
          {results ? <button onClick={onDone} style={{ ...btnPrimary, background: '#1D4E89' }}>Done</button> : <>
            <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
            <button disabled={busy || !prev || prev.every(blocked)} onClick={raise} style={{ ...btnPrimary, background: '#1D4E89' }}>
              {busy ? '…' : bills.length === 1 ? 'Raise release' : `Raise ${prev ? prev.filter(p => !blocked(p)).length : ''} releases`}</button></>}
        </div>
      </div>
    </div>
  );
}

function NewPurchaseInvModal({ pos, companies = [], company, canSwitch, onClose, onSaved }) {
  // Blank, not 5 (mig 196). A purchase invoice TRANSCRIBES a vendor's bill, so a default
  // would state a slab nobody read off it — the same literal-zero problem in a different
  // costume. Left blank the field is omitted from the POST and the column stays NULL.
  const [form, setForm] = useState({ invoice_date: today(), gst_pct: '', company_id: company || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // LINES (Rajan, 3 Oct 2026): a bill without lines is a claim, not a bill, and can never be
  // certified. Each line bills one receipt of the chosen PO, with the BILL's quantity and rate —
  // typed here, never assumed from the receipt. Received and rejected come from the GRN.
  const [grnsForPo, setGrnsForPo] = useState(null);   // null = no PO yet / loading
  const [grnErr, setGrnErr] = useState('');
  const [lines, setLines] = useState({});              // grn_id → { on, invoiced_qty, rate, gst_pct }
  useEffect(() => {
    setLines({}); setGrnsForPo(null); setGrnErr('');
    if (!form.po_no) return;
    api.get('/finance/purchase-invoices/billable-grns?po_no=' + encodeURIComponent(form.po_no))
      .then(r => setGrnsForPo(r.data.data || []))
      .catch(e => { setGrnsForPo([]); setGrnErr(e.response?.data?.error || 'Could not read the receipts for this PO.'); });
  }, [form.po_no]);
  const setLine = (id, patch) => setLines(l => ({ ...l, [id]: { ...(l[id] || {}), ...patch } }));
  const chosen = Object.entries(lines).filter(([, v]) => v.on);
  const lineBlocker = !form.po_no ? null
    : grnsForPo === null ? 'Reading the receipts for this PO…'
    : grnsForPo.length === 0 ? `No receipt on ${form.po_no} is waiting for a bill. A bill is certified against what arrived, so the receipt must be recorded first (Store → Goods receipt) — or it is already on a bill.`
    : chosen.length === 0 ? 'Tick the receipt(s) this bill is for and enter the quantity the bill states for each.'
    : chosen.some(([, v]) => !(num(v.invoiced_qty) > 0)) ? 'Enter the quantity the bill states on every ticked line.' : null;
  const onPo = (poNo) => {
    const p = pos.find(x => x.po_no === poNo);
    if (p) setForm(f => ({ ...f, po_no: poNo, supplier: p.supplier_name || f.supplier, gpo_no: p.gpo_no || f.gpo_no, po_type: p.material_type || f.po_type, amount: p.total_amount || f.amount, description: p.description || f.description }));
    else setForm(f => ({ ...f, po_no: poNo }));
  };
  const gst = num(form.amount) * num(form.gst_pct) / 100;
  const save = async () => {
    // Mirrors the server rule (POST /finance/purchase-invoices) so the requirement shows up
    // before the round-trip. The server is the authority — this is only the courtesy copy.
    if (!form.po_no) { setError('Pick the PO this invoice is against — a purchase invoice must cite a live purchase order.'); return; }
    if (lineBlocker) { setError(lineBlocker); return; }
    setBusy(true); setError('');
    const payloadLines = chosen.map(([grn_id, v]) => ({ grn_id, invoiced_qty: num(v.invoiced_qty),
      ...(String(v.gst_pct ?? '').trim() === '' ? {} : { gst_pct: num(v.gst_pct) }) }));
    try { await api.post('/finance/purchase-invoices', { ...form, lines: payloadLines, ...(String(form.gst_pct).trim() === '' ? { gst_pct: undefined } : {}) }); onSaved(); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 640, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>＋ New Purchase Invoice</h3></div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>PO * (auto-fills)</label>
            <select style={{ ...inputStyle, width: '100%', borderColor: form.po_no ? undefined : '#E0A0A0' }} value={form.po_no || ''} onChange={e => onPo(e.target.value)}>
              <option value="">— required —</option>{pos.map(p => <option key={p.id} value={p.po_no}>{p.po_no} · {p.supplier_name || ''}</option>)}</select></div>
          {/* THE BOOK, and ONLY where nothing else decides it. A bill with a PO takes that PO's
              company server-side (companyOfPurchase), so this is disabled the moment a PO is
              named — two controls over one fact is how they come to disagree. It matters
              because 61 of 398 invoices carry neither a PO nor a GPO: roughly one in seven has
              nothing to derive from, and before this they silently landed on the raiser's own
              book. The server re-checks the choice against what this account may act for; the
              form is not the authority. */}
          <div><label style={labelStyle}>Book {form.po_no ? '(set by the PO)' : '*'}</label>
            <select style={{ ...inputStyle, width: '100%', background: (form.po_no || !canSwitch) ? '#f5f5f5' : undefined }}
              value={form.company_id || ''} disabled={!!form.po_no || !canSwitch}
              title={form.po_no ? 'The purchase order decides which book this bill belongs to.' : ''}
              onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          {/* Phase 1c — the same PartyTypeahead the Day Book uses, not a 129-option <select>.
              It searches, shows "did you mean" near matches without applying them, and creates
              inline so there is no dead end. Its endpoint (/finance/daybook/payees) filters
              deleted_at, so a merged-away party is never offered. `supplier` follows the picked
              name, so the display column and the FK cannot drift. */}
          <div><label style={labelStyle}>Payee *</label>
            <PartyTypeahead
              value={form.payee_id || null}
              initialLabel={form.supplier || ''}
              placeholder="Search or create a party…"
              onChange={(id, party) => setForm({ ...form, payee_id: id || null, supplier: party?.name || form.supplier })}
            /></div>
          {[['invoice_no', 'Invoice No *'], ['supplier', 'Supplier'], ['gpo_no', 'GPO'], ['po_type', 'PO Type'], ['payment_terms', 'Payment Terms']].map(([k, l]) => (
            <div key={k}><label style={labelStyle}>{l}</label><input style={{ ...inputStyle, width: '100%' }} value={form[k] || ''} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
          ))}
          {[['invoice_date', 'Invoice Date', 'date'], ['due_date', 'Due Date', 'date'], ['amount', 'Amount ₹ *', 'number'], ['gst_pct', 'GST %', 'number']].map(([k, l, t]) => (
            <div key={k}><label style={labelStyle}>{l}</label><input type={t} style={{ ...inputStyle, width: '100%' }} value={form[k] ?? ''} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
          ))}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Lines * — one per receipt this bill is for</label>
            {!form.po_no ? <div style={{ fontSize: 11, color: '#888' }}>Pick the PO first; its receipts waiting for a bill are listed here.</div>
              : grnErr ? <div style={{ fontSize: 11, color: '#A32D2D' }}>{grnErr}</div>
              : grnsForPo === null ? <div style={{ fontSize: 11, color: '#888' }}>Reading…</div>
              : grnsForPo.length === 0 ? <div style={{ fontSize: 11, color: '#A32D2D' }}>{lineBlocker}</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead><tr>{['', 'Receipt', 'Received', 'Bill qty *', 'PO rate ₹', 'GST %'].map(h => <th key={h} style={{ textAlign: 'left', padding: '4px 5px', color: '#666', borderBottom: '1px solid #eee' }}>{h}</th>)}</tr></thead>
                  <tbody>{grnsForPo.map(g => { const v = lines[g.id] || {}; return (
                    <tr key={g.id}>
                      <td style={{ padding: '3px 5px' }}><input type="checkbox" checked={!!v.on} onChange={e => setLine(g.id, { on: e.target.checked })} /></td>
                      <td style={{ padding: '3px 5px', fontFamily: 'monospace' }}>{g.grn_no}<div style={{ fontFamily: 'inherit', color: '#888' }}>{fmtD(g.received_date)} · QC {g.quality_status}</div></td>
                      <td style={{ padding: '3px 5px' }}>{num(g.received_qty)}{num(g.rejected_qty) ? ` (rej ${num(g.rejected_qty)})` : ''}</td>
                      <td style={{ padding: '3px 5px' }}><input type="number" disabled={!v.on} style={{ ...inputStyle, width: 80 }} value={v.invoiced_qty ?? ''} placeholder="as billed" onChange={e => setLine(g.id, { invoiced_qty: e.target.value })} /></td>
                      <td style={{ padding: '3px 5px', color: '#555' }} title="The line is priced at the PO rate; the bill's own figure is the Amount above, and certification compares the two.">{g.unit_price ? num(g.unit_price) : '—'}</td>
                      <td style={{ padding: '3px 5px' }}><input type="number" disabled={!v.on} style={{ ...inputStyle, width: 60 }} value={v.gst_pct ?? ''} placeholder={String(form.gst_pct || '') || '—'} onChange={e => setLine(g.id, { gst_pct: e.target.value })} /></td>
                    </tr>); })}</tbody>
                </table>)}
            {form.po_no && grnsForPo && grnsForPo.length > 0 && lineBlocker && <div style={{ fontSize: 11, color: '#7A5A00', marginTop: 4 }}>{lineBlocker}</div>}
          </div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Description</label><input style={{ ...inputStyle, width: '100%' }} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1', background: '#F7F6F3', borderRadius: 8, padding: '8px 12px', fontSize: 12, display: 'flex', gap: 16 }}>
            <span>GST: <strong>{inr(gst)}</strong></span><span>Total: <strong style={{ color: '#3B3A8F', fontSize: 14 }}>{inr(num(form.amount) + gst)}</strong></span></div>
          <div style={{ gridColumn: '1 / -1' }}>
            <FileBtn label={form.file_url ? '✅ PDF attached' : '📎 Upload invoice PDF'} onUploaded={(u) => setForm({ ...form, file_url: u })} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          {lineBlocker && form.po_no && <span style={{ fontSize: 11, color: '#888', alignSelf: 'center' }}>{lineBlocker}</span>}
          <button disabled={busy || !!lineBlocker} onClick={save} style={{ ...btnPrimary, ...(busy || lineBlocker ? { background: '#ccc', cursor: 'not-allowed' } : {}) }}>{busy ? '…' : '💾 Create Invoice'}</button>
        </div>
      </div>
    </div>
  );
}
